import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area
} from "recharts";
import {
  Activity,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Eye,
  Crosshair,
  Shield,
  Radio,
  Cpu,
  Search,
  RefreshCw,
  Trash2,
  Zap,
  Target,
  Brain,
  Bell,
  Check,
  ChevronDown,
  Loader2,
  Plus,
  TrendingUp,
  TrendingDown,
  Minus,
  GitCompare,
  FileText,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  BarChart3,
  Radar,
  Play,
  Pause,
  Settings,
  BellRing,
  X,
  Youtube,
  Users,
  Video,
  ThumbsUp,
  MessageCircle,
  Send,
  MessageSquare,
  Copy,
  Menu,
  Database,
  Lock,
  Instagram,
  ImageIcon,
  BookOpen,
  Globe
} from "lucide-react";
import { Button } from "./components/ui/button";
import { ScrollArea } from "./components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { Progress } from "./components/ui/progress";
import { Switch } from "./components/ui/switch";
import { LearnAlgoEngine } from "./components/LearnAlgoEngine";
import { GlobalScan } from "./components/GlobalScan";

// Use REACT_APP_BACKEND_URL if set, otherwise use same origin (for Railway deployment)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Navigation items
const navItems = [
  { id: "dashboard", label: "COMMAND CENTER", icon: Activity },
  { id: "channels", label: "TARGETS", icon: Crosshair },
  { id: "youtube", label: "YOUTUBE ANALYTICS", icon: Youtube },
  { id: "instagram", label: "INSTAGRAM ANALYTICS", icon: Instagram },
  { id: "military", label: "MILITARY SCAN", icon: Radar },
  { id: "algorithm", label: "ALGORITHM POV", icon: Eye },
  { id: "timeline", label: "SUPPRESSION TIMELINE", icon: Clock },
  { id: "rootcause", label: "ROOT CAUSE ANALYSIS", icon: FileText },
  { id: "compare", label: "CHANNEL COMPARISON", icon: GitCompare },
  { id: "notifications", label: "NOTIFICATIONS", icon: BellRing },
  { id: "telegram", label: "TELEGRAM ALERTS", icon: Send },
  { id: "scheduler", label: "AUTO SCHEDULER", icon: Settings },
  { id: "ai", label: "AI CONSOLE", icon: Brain },
  { id: "learnalgo", label: "LEARN ALGO", icon: BookOpen },
  { id: "globalscan", label: "GLOBAL SCAN", icon: Globe },
];

// Threat level colors
const threatColors = {
  critical: "text-red-500 border-red-500 bg-red-500/10",
  CRITICAL: "text-red-500 border-red-500 bg-red-500/10",
  high: "text-amber-500 border-amber-500 bg-amber-500/10",
  HIGH: "text-amber-500 border-amber-500 bg-amber-500/10",
  medium: "text-purple-500 border-purple-500 bg-purple-500/10",
  MEDIUM: "text-purple-500 border-purple-500 bg-purple-500/10",
  low: "text-green-500 border-green-500 bg-green-500/10",
  LOW: "text-green-500 border-green-500 bg-green-500/10",
  unknown: "text-zinc-500 border-zinc-500 bg-zinc-500/10",
};

const TrendIcon = ({ trend }) => {
  if (trend === "IMPROVING") return <TrendingUp className="w-4 h-4 text-green-500" />;
  if (trend === "DECLINING") return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-zinc-500" />;
};

const TestResultBadge = ({ passed }) => {
  if (passed) {
    return (
      <span className="flex items-center gap-1 text-green-500 text-xs">
        <CheckCircle className="w-3 h-3" /> PASS
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-red-500 text-xs">
      <XCircle className="w-3 h-3" /> FAIL
    </span>
  );
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-700 p-3 rounded-none">
        <p className="font-mono text-xs text-zinc-400">{label}</p>
        <p className="font-mono text-sm text-cyan-400">
          Score: {payload[0].value?.toFixed(1)}
        </p>
        {payload[0].payload?.threat && (
          <p className={`font-mono text-xs ${threatColors[payload[0].payload.threat]?.split(' ')[0] || 'text-zinc-500'}`}>
            Threat: {payload[0].payload.threat}
          </p>
        )}
      </div>
    );
  }
  return null;
};

function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile sidebar
  const [activeView, setActiveView] = useState("dashboard");
  const [channels, setChannels] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [aiModel, setAiModel] = useState("claude");
  const [scanInProgress, setScanInProgress] = useState(false);
  const [showAddChannel, setShowAddChannel] = useState(false);
  const [newChannel, setNewChannel] = useState({ platform: "youtube", handle: "", url: "" });
  const [quickUrl, setQuickUrl] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolvedPreview, setResolvedPreview] = useState(null);
  
  // New state
  const [militaryScanResult, setMilitaryScanResult] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [rootCause, setRootCause] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [competitorInput, setCompetitorInput] = useState("");
  const [competitors, setCompetitors] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [youtubeAnalytics, setYoutubeAnalytics] = useState(null);
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [telegramChatId, setTelegramChatId] = useState("");
  
  // YouTube OAuth state
  const [youtubeOAuthStatus, setYoutubeOAuthStatus] = useState(null);
  const [youtubeDetailedAnalytics, setYoutubeDetailedAnalytics] = useState(null);
  
  // Instagram OAuth state
  const [instagramOAuthStatus, setInstagramOAuthStatus] = useState(null);
  const [instagramAnalytics, setInstagramAnalytics] = useState(null);
  const [instagramPerspective, setInstagramPerspective] = useState(null);
  const [instagramNicheIntel, setInstagramNicheIntel] = useState(null);
  
  // Algorithm Intelligence state
  const [algorithmPerspective, setAlgorithmPerspective] = useState(null);
  const [nicheIntel, setNicheIntel] = useState(null);
  const [thumbnailAnalysis, setThumbnailAnalysis] = useState(null);
  const [algorithmComparison, setAlgorithmComparison] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [channelThumbnails, setChannelThumbnails] = useState(null);
  const [thumbnailsLoading, setThumbnailsLoading] = useState(false);
  const [analyzingThumbId, setAnalyzingThumbId] = useState(null);
  const [abMode, setAbMode] = useState(false);
  const [abSelections, setAbSelections] = useState([null, null]);
  const [abResult, setAbResult] = useState(null);
  const [abLoading, setAbLoading] = useState(false);

  // Copy to clipboard helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("COPIED TO CLIPBOARD", { description: "Report ready for text-to-speech" });
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  // Format report for TTS (Text-to-Speech friendly)
  const formatReportForTTS = (data, type = "scan") => {
    if (!data) return "";
    
    let report = "";
    const now = new Date().toLocaleString();
    
    if (type === "scan" && data.analysis) {
      const analysis = data.analysis;
      report = `
TSV VICTORY TWISTER - MILITARY INTELLIGENCE REPORT
Generated: ${now}

EXECUTIVE SUMMARY
Target Handle: ${analysis.handle || 'Unknown'}
Threat Level: ${data.threat_level || analysis.threat_level || 'Unknown'}
Visibility Score: ${data.visibility_score || analysis.aggregate_score || 0} out of 100
Confidence Level: ${data.confidence || analysis.confidence || 0} percent

TEST RESULTS BREAKDOWN

`;
      
      if (analysis.tests) {
        Object.entries(analysis.tests).forEach(([testName, testData]) => {
          const status = testData.passed ? "PASSED" : "FAILED";
          report += `Test: ${testName.replace(/_/g, ' ').toUpperCase()}
Status: ${status}
Confidence: ${testData.confidence?.toFixed(1) || 0} percent
Severity: ${testData.severity || 'Unknown'}

`;
        });
      }
      
      if (analysis.cross_reference) {
        report += `CROSS-REFERENCE VERIFICATION

`;
        Object.entries(analysis.cross_reference).forEach(([engine, data]) => {
          const found = data.found ? "Target Found" : "Target Not Found";
          report += `${engine.toUpperCase()}: ${found}
`;
        });
      }
      
      if (data.ai_analysis) {
        report += `

AI ANALYSIS AND RECOMMENDATIONS

${data.ai_analysis}
`;
      }
      
      report += `

END OF REPORT
Generated by Victoria Black - TSV Victory Twister Military Intelligence System
`;
    } else if (type === "youtube" && data) {
      report = `
TSV VICTORY TWISTER - YOUTUBE ANALYTICS REPORT
Generated: ${now}

CHANNEL OVERVIEW
Channel Name: ${data.channel?.title || 'Unknown'}
Channel Handle: ${data.handle || 'Unknown'}
Channel ID: ${data.channel_id || 'Unknown'}

STATISTICS
Subscribers: ${data.metrics?.subscribers?.toLocaleString() || 0}
Total Views: ${data.metrics?.total_views?.toLocaleString() || 0}
Total Videos: ${data.metrics?.video_count || 0}
Average Views Per Video: ${data.metrics?.avg_views_per_video?.toFixed(0) || 0}
Engagement Rate: ${data.metrics?.avg_engagement_rate?.toFixed(2) || 0} percent

SEARCH VISIBILITY
Found in YouTube Search: ${data.search_visibility?.found_in_search ? 'Yes' : 'No'}
Search Position: ${data.search_visibility?.search_position || 'Not ranked'}
Total Search Results: ${data.search_visibility?.total_search_results || 0}

ALGORITHM SIGNALS
Hidden Subscriber Count: ${data.algorithm_signals?.hidden_subscribers ? 'Yes' : 'No'}
Made for Kids Content: ${data.algorithm_signals?.made_for_kids ? 'Yes' : 'No'}
Long Uploads Enabled: ${data.algorithm_signals?.long_uploads_enabled ? 'Yes' : 'No'}
Channel Keywords Set: ${data.algorithm_signals?.has_keywords ? 'Yes' : 'No'}

RECENT VIDEO PERFORMANCE
`;
      
      if (data.recent_videos && data.recent_videos.length > 0) {
        data.recent_videos.forEach((video, i) => {
          report += `
Video ${i + 1}: ${video.title}
Views: ${video.statistics?.views?.toLocaleString() || 0}
Likes: ${video.statistics?.likes?.toLocaleString() || 0}
Comments: ${video.statistics?.comments?.toLocaleString() || 0}
Published: ${new Date(video.published_at).toLocaleDateString()}
`;
        });
      }
      
      report += `

END OF REPORT
Generated by Victoria Black - TSV Victory Twister Military Intelligence System
`;
    }
    
    return report.trim();
  };
  const [telegramTestMessage, setTelegramTestMessage] = useState("");

  // Fetch Telegram Status
  const fetchTelegramStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/telegram/status`);
      setTelegramStatus(res.data);
    } catch (e) {
      console.error("Failed to fetch Telegram status:", e);
    }
  }, []);

  // Subscribe to Telegram
  const subscribeTelegram = async () => {
    if (!telegramChatId.trim()) {
      toast.error("Enter your Telegram Chat ID");
      return;
    }
    try {
      const res = await axios.post(`${API}/telegram/subscribe?chat_id=${telegramChatId}`);
      toast.success("SUBSCRIBED TO TELEGRAM", { description: "You will now receive alerts via Telegram" });
      await fetchTelegramStatus();
      setTelegramChatId("");
    } catch (e) {
      toast.error("Subscription failed", { description: e.response?.data?.detail || e.message });
    }
  };

  // Send test Telegram message
  const sendTestTelegram = async () => {
    if (!telegramChatId.trim()) {
      toast.error("Enter your Telegram Chat ID");
      return;
    }
    try {
      const msg = telegramTestMessage || "Test notification from TSVVictoryTwister";
      await axios.post(`${API}/telegram/test?chat_id=${telegramChatId}&message=${encodeURIComponent(msg)}`);
      toast.success("TEST MESSAGE SENT", { description: "Check your Telegram" });
    } catch (e) {
      toast.error("Failed to send test", { description: e.response?.data?.detail || e.message });
    }
  };

  // Fetch YouTube Analytics
  const fetchYoutubeAnalytics = async (handle) => {
    setLoading(true);
    setYoutubeAnalytics(null);
    try {
      const res = await axios.get(`${API}/youtube/analytics/${handle}`);
      setYoutubeAnalytics(res.data);
      toast.success("YOUTUBE DATA LOADED", { description: `${res.data.metrics?.subscribers} subscribers` });
    } catch (e) {
      toast.error("Failed to load YouTube data", { description: e.response?.data?.detail || e.message });
    }
    setLoading(false);
  };

  // Fetch data
  const fetchChannels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/channels`);
      setChannels(res.data);
    } catch (e) {
      console.error("Failed to fetch channels:", e);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/alerts?acknowledged=false`);
      setAlerts(res.data);
    } catch (e) {
      console.error("Failed to fetch alerts:", e);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      setStats(res.data);
    } catch (e) {
      console.error("Failed to fetch stats:", e);
    }
  }, []);

  const fetchScans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scans?limit=20`);
      setScans(res.data);
    } catch (e) {
      console.error("Failed to fetch scans:", e);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifs, count] = await Promise.all([
        axios.get(`${API}/notifications?limit=20`),
        axios.get(`${API}/notifications/count`)
      ]);
      setNotifications(notifs.data);
      setNotificationCount(count.data.unread_count);
    } catch (e) {
      console.error("Failed to fetch notifications:", e);
    }
  }, []);

  const fetchSchedulerStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scheduler`);
      setSchedulerStatus(res.data);
    } catch (e) {
      console.error("Failed to fetch scheduler:", e);
    }
  }, []);

  const initializeChannels = async () => {
    try {
      await axios.post(`${API}/init`);
      await fetchChannels();
      toast.success("TARGETS INITIALIZED", { description: "Military grade system ready" });
    } catch (e) {
      console.error("Init failed:", e);
    }
  };

  useEffect(() => {
    fetchChannels();
    fetchAlerts();
    fetchStats();
    fetchScans();
    fetchNotifications();
    fetchSchedulerStatus();
    fetchTelegramStatus();
    fetchYoutubeOAuthStatus();
    fetchInstagramOAuthStatus();
    initializeChannels();
    
    // Handle YouTube OAuth redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get('youtube_connected') === 'true') {
      const channelName = params.get('channel') || 'your channel';
      // Delay to ensure this toast appears after initialization toasts
      setTimeout(() => {
        toast.success("YOUTUBE ANALYTICS CONNECTED", {
          description: `Successfully linked to ${channelName}. You can now access CTR, watch time, and retention data.`,
          duration: 8000
        });
      }, 2000);
      // Clean up URL params
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh OAuth status
      setTimeout(() => fetchYoutubeOAuthStatus(), 1000);
    }
    
    // Poll for notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchSchedulerStatus();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // YouTube OAuth functions
  const fetchYoutubeOAuthStatus = async () => {
    try {
      const res = await axios.get(`${API}/youtube/oauth/status`);
      setYoutubeOAuthStatus(res.data);
    } catch (e) {
      console.error("Failed to fetch YouTube OAuth status:", e);
    }
  };

  const connectYoutubeAnalytics = async () => {
    try {
      const redirectUri = `${window.location.origin}/api/youtube/oauth/callback`;
      const res = await axios.get(`${API}/youtube/oauth/url`, {
        params: { redirect_uri: redirectUri }
      });
      
      // Open OAuth popup or redirect
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error("Failed to start YouTube connection", { description: e.response?.data?.detail || e.message });
    }
  };

  const disconnectYoutubeAnalytics = async () => {
    try {
      await axios.post(`${API}/youtube/oauth/disconnect`);
      setYoutubeOAuthStatus({ connected: false });
      setYoutubeDetailedAnalytics(null);
      toast.success("YouTube Analytics disconnected");
    } catch (e) {
      toast.error("Failed to disconnect", { description: e.message });
    }
  };

  const fetchDetailedYoutubeAnalytics = async (channelId) => {
    setLoading(true);
    setYoutubeDetailedAnalytics(null);
    try {
      const res = await axios.get(`${API}/youtube/analytics/detailed/${channelId}`, {
        params: { days: 30 }
      });
      setYoutubeDetailedAnalytics(res.data);
      toast.success("DETAILED ANALYTICS LOADED", {
        description: `CTR: ${res.data.summary?.average_ctr_percent}% | Watch Time: ${res.data.summary?.total_watch_time_hours}h`
      });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (detail.includes("not connected")) {
        toast.error("YOUTUBE NOT CONNECTED", { 
          description: "Connect your YouTube account to access CTR, watch time, and retention data.",
          duration: 8000
        });
      } else {
        toast.error("Failed to load analytics", { description: detail });
      }
    }
    setLoading(false);
  };

  // Instagram functions
  const fetchInstagramOAuthStatus = async () => {
    try {
      const res = await axios.get(`${API}/instagram/oauth/status`);
      setInstagramOAuthStatus(res.data);
    } catch (e) {
      console.error("Failed to fetch Instagram OAuth status:", e);
    }
  };

  const connectInstagram = async () => {
    try {
      const redirectUri = `${window.location.origin}/api/instagram/oauth/callback`;
      const res = await axios.get(`${API}/instagram/oauth/url`, {
        params: { redirect_uri: redirectUri }
      });
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error("Failed to start Instagram connection", { description: e.response?.data?.detail || e.message });
    }
  };

  const disconnectInstagram = async () => {
    try {
      await axios.post(`${API}/instagram/oauth/disconnect`);
      setInstagramOAuthStatus({ connected: false });
      setInstagramAnalytics(null);
      toast.success("Instagram disconnected");
    } catch (e) {
      toast.error("Failed to disconnect", { description: e.message });
    }
  };

  const fetchInstagramAnalytics = async (channelId) => {
    setLoading(true);
    setInstagramAnalytics(null);
    try {
      const res = await axios.get(`${API}/instagram/analytics/${channelId}`, {
        params: { days: 30 }
      });
      setInstagramAnalytics(res.data);
      toast.success("INSTAGRAM ANALYTICS LOADED", {
        description: `Engagement Rate: ${res.data.summary?.engagement_rate_percent}% | Reach: ${res.data.summary?.total_reach?.toLocaleString()}`
      });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (detail.includes("not connected")) {
        toast.error("INSTAGRAM NOT CONNECTED", { 
          description: "Connect your Instagram Business account to access analytics.",
          duration: 8000
        });
      } else {
        toast.error("Failed to load Instagram analytics", { description: detail });
      }
    }
    setLoading(false);
  };

  const fetchInstagramPerspective = async (channelId) => {
    setLoading(true);
    setInstagramPerspective(null);
    try {
      const res = await axios.get(`${API}/instagram/algorithm-perspective/${channelId}`);
      setInstagramPerspective(res.data);
      toast.success("INSTAGRAM ALGORITHM PERSPECTIVE LOADED", {
        description: `Engagement: ${res.data.engagement_rating}`
      });
    } catch (e) {
      toast.error("Failed to load perspective", { description: e.response?.data?.detail || e.message });
    }
    setLoading(false);
  };

  const fetchInstagramNicheIntel = async () => {
    setLoading(true);
    setInstagramNicheIntel(null);
    try {
      const res = await axios.get(`${API}/instagram/niche-intel`);
      setInstagramNicheIntel(res.data);
      toast.success("INSTAGRAM NICHE INTEL GATHERED", {
        description: "2026 tactics loaded"
      });
    } catch (e) {
      toast.error("Failed to get niche intel", { description: e.response?.data?.detail || e.message });
    }
    setLoading(false);
  };

  // Military scan
  const runMilitaryScan = async (channelId) => {
    setScanInProgress(true);
    setSelectedChannel(channelId);
    setMilitaryScanResult(null);
    try {
      const res = await axios.post(`${API}/scan/military/${channelId}`);
      setMilitaryScanResult(res.data);
      toast.success("MILITARY SCAN COMPLETE", {
        description: `Threat Level: ${res.data.threat_level} | Score: ${res.data.visibility_score}/100`
      });
      await Promise.all([fetchScans(), fetchAlerts(), fetchStats(), fetchNotifications()]);
    } catch (e) {
      toast.error("SCAN FAILED", { description: e.message });
    }
    setScanInProgress(false);
  };

  // Fetch timeline
  const fetchTimeline = async (channelId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/timeline/${channelId}?days=30`);
      setTimeline(res.data);
    } catch (e) {
      toast.error("Failed to fetch timeline");
    }
    setLoading(false);
  };

  // Fetch root cause
  const fetchRootCause = async (channelId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rootcause/${channelId}`);
      setRootCause(res.data);
    } catch (e) {
      toast.error("No root cause analysis found. Run a military scan first.");
      setRootCause(null);
    }
    setLoading(false);
  };

  // Run comparison
  const runComparison = async () => {
    if (!selectedChannel || competitors.length === 0) {
      toast.error("Select a target channel and add competitors");
      return;
    }
    setLoading(true);
    setComparisonResult(null);
    try {
      const res = await axios.post(`${API}/compare`, {
        target_channel_id: selectedChannel,
        competitor_handles: competitors
      });
      setComparisonResult(res.data);
      toast.success("COMPARISON COMPLETE");
    } catch (e) {
      toast.error("Comparison failed", { description: e.message });
    }
    setLoading(false);
  };

  // Scheduler controls
  const toggleScheduler = async () => {
    try {
      if (schedulerStatus?.running) {
        await axios.post(`${API}/scheduler/stop`);
        toast.success("SCHEDULER STOPPED");
      } else {
        await axios.post(`${API}/scheduler/start`);
        toast.success("SCHEDULER STARTED", { description: "Automatic scans are now active" });
      }
      await fetchSchedulerStatus();
    } catch (e) {
      toast.error("Failed to toggle scheduler");
    }
  };

  const configureScheduler = async (enabled, interval) => {
    try {
      await axios.post(`${API}/scheduler/configure?enabled=${enabled}&interval_hours=${interval}`);
      await fetchSchedulerStatus();
      toast.success("SCHEDULER CONFIGURED");
    } catch (e) {
      toast.error("Failed to configure scheduler");
    }
  };

  // Notification actions
  const markNotificationRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`);
      await fetchNotifications();
    } catch (e) {
      console.error("Failed to mark read:", e);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      await fetchNotifications();
      toast.success("All notifications marked as read");
    } catch (e) {
      toast.error("Failed to mark all read");
    }
  };

  // Add competitor
  const addCompetitor = () => {
    if (competitorInput.trim() && !competitors.includes(competitorInput.trim())) {
      setCompetitors([...competitors, competitorInput.trim()]);
      setCompetitorInput("");
    }
  };

  // AI Analysis
  const runAIAnalysis = async () => {
    if (!aiPrompt.trim()) return;
    setLoading(true);
    setAiResponse("");
    try {
      const res = await axios.post(`${API}/analyze/raw?model=${aiModel}`, aiPrompt, {
        headers: { "Content-Type": "text/plain" }
      });
      setAiResponse(res.data.analysis);
    } catch (e) {
      toast.error("AI ANALYSIS FAILED", { description: e.message });
    }
    setLoading(false);
  };

  // Acknowledge alert
  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/acknowledge`);
      await fetchAlerts();
      toast.success("ALERT ACKNOWLEDGED");
    } catch (e) {
      toast.error("Failed to acknowledge alert");
    }
  };

  // Add channel
  const addChannel = async () => {
    const handleRaw = (newChannel.handle || "").trim();
    if (!handleRaw) {
      toast.error("Handle is required");
      return;
    }
    try {
      const res = await axios.post(`${API}/channels`, {
        ...newChannel,
        handle: handleRaw,
        url: (newChannel.url || "").trim(),
      });
      await fetchChannels();
      setShowAddChannel(false);
      setNewChannel({ platform: "youtube", handle: "", url: "" });
      setQuickUrl("");
      setResolvedPreview(null);
      const title = res?.data?.title;
      toast.success("TARGET ADDED", {
        description: title ? `Confirmed on YouTube: ${title}` : `Handle: ${handleRaw}`,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error("Failed to add channel", { description: String(detail).slice(0, 220) });
    }
  };

  // Resolve any YouTube URL into a preview (video / channel / @handle / share link)
  const resolveQuickUrl = async () => {
    const url = (quickUrl || "").trim();
    if (!url) {
      toast.error("Paste a YouTube URL or handle first");
      return;
    }
    setResolving(true);
    setResolvedPreview(null);
    try {
      const res = await axios.post(`${API}/channels/resolve`, { url });
      const d = res.data;
      setResolvedPreview(d);
      // Prefill the form so the user can see + edit before confirming
      setNewChannel({ platform: "youtube", handle: d.handle || "", url: d.url || "" });
      toast.success(`Detected: ${d.title || d.handle}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Could not detect a channel";
      toast.error("Detect failed", { description: String(detail).slice(0, 220) });
    } finally {
      setResolving(false);
    }
  };

  // Delete channel
  const deleteChannel = async (channelId) => {
    try {
      await axios.delete(`${API}/channels/${channelId}`);
      await fetchChannels();
      toast.success("TARGET REMOVED");
    } catch (e) {
      toast.error("Failed to delete channel");
    }
  };

  // ============ ALGORITHM INTELLIGENCE FUNCTIONS ============
  
  // Get Algorithm's Perspective on a channel
  const fetchAlgorithmPerspective = async (channelId) => {
    setLoading(true);
    setAlgorithmPerspective(null);
    try {
      const res = await axios.get(`${API}/algorithm/perspective/${channelId}`);
      setAlgorithmPerspective(res.data);
      
      // Check if using stale cached data
      if (res.data.cache_stale) {
        toast.warning("USING CACHED DATA", { 
          description: res.data.cache_warning || "YouTube API temporarily unavailable",
          duration: 5000
        });
      } else {
        toast.success("ALGORITHM PERSPECTIVE LOADED", { 
          description: `Niche detected: ${res.data.detected_niche}` 
        });
      }
    } catch (e) {
      const errorDetail = e.response?.data?.detail || e.message;
      const isQuotaError = e.response?.status === 429 || errorDetail?.toLowerCase().includes("quota");
      
      if (isQuotaError) {
        toast.error("YOUTUBE API QUOTA EXCEEDED", { 
          description: "Daily quota resets at midnight Pacific Time. Try again later.",
          duration: 8000
        });
      } else {
        toast.error("Failed to get algorithm perspective", { description: errorDetail });
      }
    }
    setLoading(false);
  };
  
  // Get Niche Intelligence - web-based tactics discovery
  const fetchNicheIntelligence = async (channelId, forceRefresh = false) => {
    setLoading(true);
    setNicheIntel(null);
    try {
      const url = forceRefresh 
        ? `${API}/algorithm/niche-intel/${channelId}?force_refresh=true`
        : `${API}/algorithm/niche-intel/${channelId}`;
      const res = await axios.get(url);
      setNicheIntel(res.data);
      
      // Show appropriate toast based on cache status
      if (res.data.from_cache) {
        toast.success("CACHED INTELLIGENCE LOADED", { 
          description: `From ${res.data.cache_age_hours}h ago - Click refresh for new analysis`,
          duration: 5000
        });
      } else {
        toast.success("FRESH INTELLIGENCE GENERATED", { 
          description: `New analysis for ${res.data.detected_niche || 'your niche'} - Cached for 24h`,
          duration: 5000
        });
      }
    } catch (e) {
      const errorDetail = e.response?.data?.detail || e.message;
      const isQuotaError = e.response?.status === 429 || errorDetail?.toLowerCase().includes("quota");
      
      if (isQuotaError) {
        toast.error("YOUTUBE API QUOTA EXCEEDED", { 
          description: "Daily quota resets at midnight Pacific Time. Try again later.",
          duration: 8000
        });
      } else {
        toast.error("Failed to get niche intelligence", { description: errorDetail });
      }
    }
    setLoading(false);
  };
  
  // Analyze thumbnail
  const analyzeThumbnail = async (url, channelId = "", title = "", platform = "youtube") => {
    if (!url) {
      toast.error("Enter a thumbnail or video URL");
      return;
    }
    setLoading(true);
    setThumbnailAnalysis(null);
    try {
      const res = await axios.post(`${API}/algorithm/thumbnail-analysis`, null, {
        params: { thumbnail_url: url, channel_id: channelId, title, platform }
      });
      setThumbnailAnalysis(res.data);
      toast.success("THUMBNAIL ANALYZED");
    } catch (e) {
      toast.error("Thumbnail analysis failed", { description: e.message });
    }
    setLoading(false);
    setAnalyzingThumbId(null);
  };

  // Fetch thumbnails for a channel
  const fetchChannelThumbnails = async (channelId) => {
    if (!channelId) return;
    setThumbnailsLoading(true);
    setChannelThumbnails(null);
    setThumbnailAnalysis(null);
    try {
      const res = await axios.get(`${API}/algorithm/thumbnails/${channelId}`, {
        params: { limit: 12 }
      });
      setChannelThumbnails(res.data);
      toast.success(`LOADED ${res.data.count} THUMBNAILS`, {
        description: `From ${res.data.platform === 'youtube' ? 'YouTube' : 'Instagram'}`
      });
    } catch (e) {
      toast.error("Failed to load thumbnails", { description: e.response?.data?.detail || e.message });
    }
    setThumbnailsLoading(false);
  };

  // A/B Thumbnail Comparison
  const runAbComparison = async () => {
    const [a, b] = abSelections;
    if (!a || !b) {
      toast.error("Select two thumbnails to compare");
      return;
    }
    setAbLoading(true);
    setAbResult(null);
    try {
      const res = await axios.post(`${API}/algorithm/thumbnail-compare`, null, {
        params: {
          thumbnail_a_url: a.thumbnail_url,
          thumbnail_b_url: b.thumbnail_url,
          title_a: a.title || "",
          title_b: b.title || "",
          platform: channelThumbnails?.platform || "youtube",
          channel_id: channelThumbnails?.channel_id || ""
        }
      });
      setAbResult(res.data);
      toast.success("A/B COMPARISON COMPLETE");
    } catch (e) {
      toast.error("A/B comparison failed", { description: e.response?.data?.detail || e.message });
    }
    setAbLoading(false);
  };

  const handleThumbnailClick = (thumb) => {
    if (abMode) {
      // A/B selection mode
      const [a, b] = abSelections;
      if (!a) {
        setAbSelections([thumb, null]);
        toast.info("THUMBNAIL A SELECTED", { description: "Now pick Thumbnail B" });
      } else if (!b && thumb.id !== a.id) {
        setAbSelections([a, thumb]);
        toast.info("THUMBNAIL B SELECTED", { description: "Click COMPARE to run A/B test" });
      } else if (thumb.id === a?.id) {
        setAbSelections([null, null]);
      } else {
        // Reset and start over
        setAbSelections([thumb, null]);
        toast.info("THUMBNAIL A RE-SELECTED", { description: "Now pick Thumbnail B" });
      }
    } else {
      // Single analysis mode
      if (!loading && analyzingThumbId !== thumb.id) {
        setAnalyzingThumbId(thumb.id);
        analyzeThumbnail(
          thumb.thumbnail_url,
          channelThumbnails?.channel_id,
          thumb.title,
          channelThumbnails?.platform
        );
      }
    }
  };
  
  // Compare channels from algorithm's perspective
  const compareChannelAlgorithms = async (channelIds) => {
    if (channelIds.length < 2) {
      toast.error("Select at least 2 channels to compare");
      return;
    }
    setLoading(true);
    setAlgorithmComparison(null);
    try {
      const res = await axios.get(`${API}/algorithm/compare-channels`, {
        params: { channel_ids: channelIds.join(",") }
      });
      setAlgorithmComparison(res.data);
      toast.success("ALGORITHM COMPARISON COMPLETE");
    } catch (e) {
      toast.error("Comparison failed", { description: e.message });
    }
    setLoading(false);
  };

  return (
    <div className="app-container">
      <Toaster 
        theme="dark" 
        position="top-right"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid #262626',
            borderLeft: '3px solid #FF2A2A',
            color: '#FFFFFF',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '12px'
          }
        }}
      />
      
      {/* Mobile Overlay */}
      <div 
        className={`mobile-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${sidebarOpen ? 'open' : ''}`} data-testid="sidebar">
        <div className="sidebar-header">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-red-600 flex items-center justify-center">
                <Radar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-display font-bold text-sm tracking-wider text-white glitch-text">
                  TSVVICTORY
                </h1>
                <p className="font-accent text-[9px] text-red-500 tracking-widest">
                  MILITARY GRADE
                </p>
              </div>
            </div>
          )}
          <button
            onClick={() => { setSidebarCollapsed(!sidebarCollapsed); setSidebarOpen(false); }}
            className="absolute right-2 top-5 p-1 text-zinc-600 hover:text-white transition-colors hidden md:block"
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setSidebarOpen(false)}
            className="absolute right-2 top-5 p-1 text-zinc-600 hover:text-white transition-colors md:hidden"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveView(item.id); setSidebarOpen(false); }}
              className={`nav-item w-full text-left ${activeView === item.id ? 'active' : ''}`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="flex items-center gap-2">
                  {item.label}
                  {item.id === "notifications" && notificationCount > 0 && (
                    <span className="w-5 h-5 bg-red-600 rounded-full text-[10px] flex items-center justify-center">
                      {notificationCount}
                    </span>
                  )}
                </span>
              )}
            </button>
          ))}
        </nav>
        
        {!sidebarCollapsed && (
          <div className="p-4 border-t border-zinc-800">
            <div className="flex items-center gap-2 text-zinc-600">
              <Radio className={`w-3 h-3 ${schedulerStatus?.running ? 'text-green-500' : 'text-red-500'} animate-pulse`} />
              <span className="font-accent text-[10px] tracking-wider text-zinc-400">
                {schedulerStatus?.running ? 'AUTO-SCAN ACTIVE' : 'SCHEDULER OFF'}
              </span>
            </div>
          </div>
        )}
      </aside>
      
      {/* Main Content */}
      <main className="main-content">
        {/* Top Bar */}
        <header className="top-bar">
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setSidebarOpen(true)}
            className="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          
          <div className="command-prompt" data-testid="search-bar">
            <span className="prompt-symbol">&gt;</span>
            <input
              type="text"
              className="terminal-input flex-1"
              placeholder="enter_target_query..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search className="w-4 h-4 text-zinc-600" />
          </div>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={() => { fetchChannels(); fetchAlerts(); fetchStats(); fetchScans(); fetchNotifications(); }}
              className="text-zinc-600 hover:text-white transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            
            {/* Notification Bell */}
            <button 
              onClick={() => setShowNotificationPanel(!showNotificationPanel)}
              className="relative text-zinc-600 hover:text-white transition-colors"
              data-testid="notification-bell"
            >
              <Bell className="w-4 h-4" />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full text-[10px] flex items-center justify-center pulse-alert">
                  {notificationCount}
                </span>
              )}
            </button>
          </div>
        </header>
        
        {/* Notification Panel Overlay */}
        {showNotificationPanel && (
          <div className="absolute right-4 top-16 w-96 bg-zinc-900 border border-zinc-800 shadow-lg z-50" data-testid="notification-panel">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <span className="font-display font-semibold text-white">NOTIFICATIONS</span>
              <div className="flex items-center gap-2">
                <button 
                  onClick={markAllNotificationsRead}
                  className="text-xs text-zinc-500 hover:text-white"
                >
                  Mark all read
                </button>
                <button onClick={() => setShowNotificationPanel(false)} className="text-zinc-600 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <ScrollArea className="h-96">
              <div className="p-2">
                {notifications.length === 0 ? (
                  <p className="text-zinc-600 text-sm p-4 text-center">No notifications</p>
                ) : (
                  notifications.map((notif) => (
                    <div 
                      key={notif.id}
                      onClick={() => markNotificationRead(notif.id)}
                      className={`p-3 mb-2 border cursor-pointer transition-colors ${
                        notif.read 
                          ? 'border-zinc-800 bg-zinc-900/50' 
                          : 'border-red-500/30 bg-red-500/5'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          notif.severity === 'critical' ? 'text-red-500' : 
                          notif.severity === 'warning' ? 'text-amber-500' : 'text-cyan-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-xs font-semibold text-white truncate">{notif.title}</p>
                          <p className="font-mono text-[10px] text-zinc-400 mt-1">{notif.message}</p>
                          <p className="font-accent text-[9px] text-zinc-600 mt-2">
                            {new Date(notif.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        )}
        
        {/* Content Area */}
        <div className="content-area data-grid">
          
          {/* Dashboard View */}
          {activeView === "dashboard" && (
            <div className="bento-grid" data-testid="dashboard">
              {/* Stats Row */}
              <div className="widget col-span-12 md:col-span-3">
                <div className="widget-content">
                  <div className="stat-value text-red-500">{stats?.total_channels || 0}</div>
                  <div className="stat-label">TARGETS TRACKED</div>
                </div>
              </div>
              
              <div className="widget col-span-12 md:col-span-3">
                <div className="widget-content">
                  <div className="stat-value text-cyan-400">{stats?.military_scans || 0}</div>
                  <div className="stat-label">MILITARY SCANS</div>
                </div>
              </div>
              
              <div className="widget col-span-12 md:col-span-3">
                <div className="widget-content">
                  <div className="stat-value text-amber-500">{stats?.active_alerts || 0}</div>
                  <div className="stat-label">ACTIVE THREATS</div>
                </div>
              </div>
              
              <div className="widget col-span-12 md:col-span-3">
                <div className="widget-content">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full animate-pulse ${schedulerStatus?.running ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className={`stat-value text-base ${schedulerStatus?.running ? 'text-green-500' : 'text-red-500'}`}>
                      {schedulerStatus?.running ? 'AUTO-SCAN' : 'MANUAL'}
                    </span>
                  </div>
                  <div className="stat-label">SCHEDULER</div>
                </div>
              </div>
              
              {/* Tracked Channels */}
              <div className="widget col-span-12 md:col-span-6">
                <div className="widget-header">
                  <span className="widget-title">TRACKED TARGETS</span>
                  <button onClick={() => setShowAddChannel(true)} className="text-zinc-600 hover:text-red-500 transition-colors">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="widget-content space-y-3">
                  {channels.length === 0 ? (
                    <p className="text-zinc-600 text-sm">No targets. Click + to add.</p>
                  ) : (
                    channels.map((ch) => (
                      <div key={ch.id} className="channel-card" data-testid={`channel-${ch.id}`}>
                        <div className="channel-avatar">
                          {ch.handle?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-display font-semibold text-white truncate">
                            {ch.display_name || ch.handle}
                          </p>
                          <p className="font-accent text-[10px] text-zinc-600 truncate">
                            @{ch.handle} · {ch.platform?.toUpperCase()}
                          </p>
                        </div>
                        <button
                          onClick={() => runMilitaryScan(ch.id)}
                          disabled={scanInProgress}
                          className="btn-attack text-xs px-3 py-1"
                        >
                          {scanInProgress && selectedChannel === ch.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <><Radar className="w-3 h-3 inline mr-1" />SCAN</>
                          )}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              {/* Recent Notifications */}
              <div className="widget col-span-12 md:col-span-6">
                <div className="widget-header">
                  <span className="widget-title">RECENT NOTIFICATIONS</span>
                  <BellRing className="w-4 h-4 text-cyan-400" />
                </div>
                <ScrollArea className="h-[250px]">
                  <div className="widget-content space-y-2">
                    {notifications.length === 0 ? (
                      <p className="text-zinc-600 text-sm">No notifications yet.</p>
                    ) : (
                      notifications.slice(0, 5).map((notif) => (
                        <div 
                          key={notif.id} 
                          className={`p-3 border ${notif.read ? 'border-zinc-800' : 'border-red-500/30 bg-red-500/5'}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <AlertCircle className={`w-3 h-3 ${
                              notif.severity === 'critical' ? 'text-red-500' : 
                              notif.severity === 'warning' ? 'text-amber-500' : 'text-cyan-500'
                            }`} />
                            <span className="font-mono text-xs text-white">{notif.title}</span>
                          </div>
                          <p className="font-mono text-[10px] text-zinc-500">{notif.message}</p>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
              
              {/* Recent Scans */}
              <div className="widget col-span-12">
                <div className="widget-header">
                  <span className="widget-title">RECENT MILITARY INTELLIGENCE</span>
                  <Cpu className="w-4 h-4 text-cyan-400" />
                </div>
                <ScrollArea className="h-[300px]">
                  <div className="widget-content">
                    {scans.length === 0 ? (
                      <p className="text-zinc-600 text-sm">No scans yet.</p>
                    ) : (
                      <div className="space-y-4">
                        {scans.slice(0, 5).map((scan) => (
                          <div key={scan.id} className="border border-zinc-800 p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border ${threatColors[scan.threat_level] || threatColors.unknown}`}>
                                  {scan.threat_level || "UNKNOWN"}
                                </span>
                                {scan.confidence_score && (
                                  <span className="text-[10px] text-cyan-400">
                                    {scan.confidence_score.toFixed(1)}% CONFIDENCE
                                  </span>
                                )}
                              </div>
                              <span className="font-accent text-[9px] text-zinc-600">
                                {scan.scan_type?.toUpperCase()} · {new Date(scan.timestamp).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
          
          {/* Timeline View with Chart */}
          {activeView === "timeline" && (
            <div data-testid="timeline-view">
              <h2 className="font-display font-bold text-xl tracking-wider text-white mb-2">
                SUPPRESSION TIMELINE
              </h2>
              <p className="text-zinc-500 text-sm mb-6">
                Visual trend analysis. Track visibility changes and detect algorithm shifts.
              </p>
              
              {/* Channel Selection */}
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">SELECT CHANNEL</span>
                </div>
                <div className="widget-content">
                  <div className="flex flex-wrap gap-2">
                    {channels.map((ch) => (
                      <button
                        key={ch.id}
                        onClick={() => { setSelectedChannel(ch.id); fetchTimeline(ch.id); }}
                        className={`px-4 py-2 border text-xs font-mono transition-colors ${selectedChannel === ch.id ? 'border-red-500 bg-red-500/10 text-red-500' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                      >
                        @{ch.handle}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Timeline Chart */}
              {timeline && timeline.chart_data && timeline.chart_data.length > 0 && (
                <div className="widget mb-6">
                  <div className="widget-header">
                    <span className="widget-title">VISIBILITY TREND</span>
                    <div className="flex items-center gap-2">
                      <TrendIcon trend={timeline.trend} />
                      <span className={`text-xs ${timeline.trend === 'IMPROVING' ? 'text-green-500' : timeline.trend === 'DECLINING' ? 'text-red-500' : 'text-zinc-500'}`}>
                        {timeline.trend} ({timeline.score_change >= 0 ? '+' : ''}{timeline.score_change?.toFixed(1)})
                      </span>
                    </div>
                  </div>
                  <div className="widget-content">
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={timeline.chart_data}>
                          <defs>
                            <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#FF2A2A" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#FF2A2A" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                          <XAxis 
                            dataKey="date" 
                            stroke="#525252" 
                            tick={{ fill: '#525252', fontSize: 10 }}
                          />
                          <YAxis 
                            domain={[0, 100]} 
                            stroke="#525252" 
                            tick={{ fill: '#525252', fontSize: 10 }}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Area 
                            type="monotone" 
                            dataKey="score" 
                            stroke="#FF2A2A" 
                            strokeWidth={2}
                            fill="url(#scoreGradient)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Timeline Stats */}
              {timeline && (
                <div className="widget">
                  <div className="widget-header">
                    <span className="widget-title">30-DAY STATISTICS</span>
                  </div>
                  <div className="widget-content">
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="bg-zinc-900 p-4 border border-zinc-800">
                        <div className="stat-value text-xl text-cyan-400">{timeline.total_scans || 0}</div>
                        <div className="stat-label">DATA POINTS</div>
                      </div>
                      <div className="bg-zinc-900 p-4 border border-zinc-800">
                        <div className="stat-value text-xl" style={{ color: timeline.latest_score >= 60 ? '#00FF41' : '#FF2A2A' }}>
                          {timeline.latest_score?.toFixed(0) || 'N/A'}
                        </div>
                        <div className="stat-label">LATEST SCORE</div>
                      </div>
                      <div className="bg-zinc-900 p-4 border border-zinc-800">
                        <div className={`stat-value text-xl ${timeline.score_change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {timeline.score_change >= 0 ? '+' : ''}{timeline.score_change?.toFixed(1) || 0}
                        </div>
                        <div className="stat-label">SCORE CHANGE</div>
                      </div>
                      <div className="bg-zinc-900 p-4 border border-zinc-800">
                        <span className={`stat-value text-xl ${threatColors[timeline.latest_threat_level]?.split(' ')[0] || 'text-zinc-500'}`}>
                          {timeline.latest_threat_level || 'N/A'}
                        </span>
                        <div className="stat-label">THREAT LEVEL</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {!timeline && !loading && selectedChannel && (
                <div className="widget">
                  <div className="widget-content text-center py-12">
                    <Clock className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-400">No timeline data. Run military scans to build history.</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Scheduler View */}
          {activeView === "scheduler" && (
            <div data-testid="scheduler-view">
              <h2 className="font-display font-bold text-xl tracking-wider text-white mb-2">
                AUTOMATIC SCAN SCHEDULER
              </h2>
              <p className="text-zinc-500 text-sm mb-6">
                24/7 monitoring. Automatic scans detect algorithm changes while you sleep.
              </p>
              
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">SCHEDULER STATUS</span>
                  <div className={`w-3 h-3 rounded-full animate-pulse ${schedulerStatus?.running ? 'bg-green-500' : 'bg-red-500'}`} />
                </div>
                <div className="widget-content">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="font-mono text-sm text-zinc-400">Scheduler Active</span>
                        <button
                          onClick={toggleScheduler}
                          className={`px-4 py-2 border text-xs font-mono transition-all ${
                            schedulerStatus?.running 
                              ? 'border-red-500 bg-red-500/10 text-red-500 hover:bg-red-500/20' 
                              : 'border-green-500 bg-green-500/10 text-green-500 hover:bg-green-500/20'
                          }`}
                        >
                          {schedulerStatus?.running ? (
                            <><Pause className="w-3 h-3 inline mr-2" />STOP</>
                          ) : (
                            <><Play className="w-3 h-3 inline mr-2" />START</>
                          )}
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800">
                          <span className="font-mono text-xs text-zinc-400">Status</span>
                          <span className={`font-mono text-xs ${schedulerStatus?.running ? 'text-green-500' : 'text-red-500'}`}>
                            {schedulerStatus?.running ? 'RUNNING' : 'STOPPED'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800">
                          <span className="font-mono text-xs text-zinc-400">Interval</span>
                          <span className="font-mono text-xs text-cyan-400">
                            Every {schedulerStatus?.interval_hours || 6} hours
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800">
                          <span className="font-mono text-xs text-zinc-400">Last Run</span>
                          <span className="font-mono text-xs text-zinc-300">
                            {schedulerStatus?.last_run ? new Date(schedulerStatus.last_run).toLocaleString() : 'Never'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800">
                          <span className="font-mono text-xs text-zinc-400">Next Run</span>
                          <span className="font-mono text-xs text-zinc-300">
                            {schedulerStatus?.next_run ? new Date(schedulerStatus.next_run).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-display font-semibold text-white mb-4">CONFIGURE INTERVAL</h3>
                      <div className="space-y-2">
                        {[1, 3, 6, 12, 24].map((hours) => (
                          <button
                            key={hours}
                            onClick={() => configureScheduler(true, hours)}
                            className={`w-full p-3 border text-left font-mono text-xs transition-colors ${
                              schedulerStatus?.interval_hours === hours 
                                ? 'border-red-500 bg-red-500/10 text-red-500' 
                                : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                            }`}
                          >
                            Every {hours} hour{hours > 1 ? 's' : ''}
                            {hours === 6 && <span className="text-zinc-600 ml-2">(recommended)</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="widget">
                <div className="widget-header">
                  <span className="widget-title">HOW IT WORKS</span>
                </div>
                <div className="widget-content">
                  <div className="space-y-3 font-mono text-xs text-zinc-400">
                    <p>1. Scheduler runs military-grade scans on all tracked channels at the configured interval</p>
                    <p>2. Each scan performs 3x verification passes with cross-reference checks</p>
                    <p>3. Results are stored in the timeline for trend analysis</p>
                    <p>4. Notifications are automatically created for:</p>
                    <ul className="ml-4 space-y-1">
                      <li>• Score drops of 10+ points (CRITICAL)</li>
                      <li>• Threat level escalations</li>
                      <li>• Potential algorithm changes</li>
                      <li>• Scan completions</li>
                    </ul>
                    <p>5. Alerts are generated for HIGH/CRITICAL threat levels</p>
                    <p>6. <b className="text-cyan-400">Telegram notifications</b> sent for CRITICAL and WARNING alerts</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Telegram Alerts View */}
          {activeView === "telegram" && (
            <div data-testid="telegram-view">
              <h2 className="font-display font-bold text-xl tracking-wider text-white mb-2">
                TELEGRAM ALERTS
              </h2>
              <p className="text-zinc-500 text-sm mb-6">
                Receive instant notifications via Telegram from Victoria Black.
              </p>
              
              {/* Telegram Status */}
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">BOT STATUS</span>
                  <div className={`w-3 h-3 rounded-full ${telegramStatus?.enabled ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                </div>
                <div className="widget-content">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800">
                          <span className="font-mono text-xs text-zinc-400">Bot Name</span>
                          <span className="font-mono text-xs text-cyan-400">{telegramStatus?.bot_name || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800">
                          <span className="font-mono text-xs text-zinc-400">Bot Username</span>
                          <span className="font-mono text-xs text-white">@{telegramStatus?.bot_username || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800">
                          <span className="font-mono text-xs text-zinc-400">Subscribers</span>
                          <span className="font-mono text-xs text-green-400">{telegramStatus?.subscribers || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800">
                          <span className="font-mono text-xs text-zinc-400">Status</span>
                          <span className={`font-mono text-xs ${telegramStatus?.enabled ? 'text-green-500' : 'text-red-500'}`}>
                            {telegramStatus?.enabled ? 'CONNECTED' : 'DISCONNECTED'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-display font-semibold text-white mb-4">HOW TO SUBSCRIBE</h3>
                      <div className="space-y-3 font-mono text-xs text-zinc-400">
                        <p>1. Open Telegram and search for <span className="text-cyan-400">@{telegramStatus?.bot_username || 'VictoriaBlackText_bot'}</span></p>
                        <p>2. Start a conversation with the bot</p>
                        <p>3. Send any message to get your Chat ID</p>
                        <p>4. Enter your Chat ID below and subscribe</p>
                        <p className="text-amber-500 mt-2">Note: You'll receive CRITICAL and WARNING alerts automatically.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Subscribe Section */}
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">SUBSCRIBE TO ALERTS</span>
                  <Send className="w-4 h-4 text-cyan-400" />
                </div>
                <div className="widget-content">
                  <div className="flex gap-4 mb-4">
                    <input
                      type="text"
                      className="flex-1 bg-black border border-zinc-800 p-3 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-red-500 focus:outline-none"
                      placeholder="Enter your Telegram Chat ID (e.g., 123456789)"
                      value={telegramChatId}
                      onChange={(e) => setTelegramChatId(e.target.value)}
                    />
                    <button onClick={subscribeTelegram} className="btn-attack">
                      <Send className="w-4 h-4 inline mr-2" />
                      SUBSCRIBE
                    </button>
                  </div>
                  
                  <p className="text-zinc-600 text-xs">
                    Don't know your Chat ID? Send a message to the bot and check the response, or use a bot like @userinfobot
                  </p>
                </div>
              </div>
              
              {/* Test Notification */}
              <div className="widget">
                <div className="widget-header">
                  <span className="widget-title">TEST NOTIFICATION</span>
                </div>
                <div className="widget-content">
                  <div className="space-y-4">
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-800 p-3 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-red-500 focus:outline-none"
                      placeholder="Chat ID"
                      value={telegramChatId}
                      onChange={(e) => setTelegramChatId(e.target.value)}
                    />
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-800 p-3 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-red-500 focus:outline-none"
                      placeholder="Test message (optional)"
                      value={telegramTestMessage}
                      onChange={(e) => setTelegramTestMessage(e.target.value)}
                    />
                    <button onClick={sendTestTelegram} className="btn-secondary w-full">
                      <MessageSquare className="w-4 h-4 inline mr-2" />
                      SEND TEST MESSAGE
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Notifications View */}
          {activeView === "notifications" && (
            <div data-testid="notifications-view">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-display font-bold text-xl tracking-wider text-white">
                    NOTIFICATIONS
                  </h2>
                  <p className="text-zinc-500 text-sm">Algorithm change alerts and scan notifications</p>
                </div>
                <button onClick={markAllNotificationsRead} className="btn-secondary text-xs">
                  MARK ALL READ
                </button>
              </div>
              
              <div className="widget">
                <div className="widget-content">
                  {notifications.length === 0 ? (
                    <div className="text-center py-12">
                      <Bell className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                      <p className="text-zinc-400">No notifications yet. Run scans or enable the scheduler.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notifications.map((notif) => (
                        <div 
                          key={notif.id}
                          className={`p-4 border transition-colors ${
                            notif.read 
                              ? 'border-zinc-800 bg-zinc-900/50' 
                              : 'border-l-2 border-l-red-500 border-zinc-800 bg-red-500/5'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                                notif.severity === 'critical' ? 'text-red-500' : 
                                notif.severity === 'warning' ? 'text-amber-500' : 'text-cyan-500'
                              }`} />
                              <div>
                                <p className="font-display font-semibold text-white">{notif.title}</p>
                                <p className="font-mono text-xs text-zinc-400 mt-1">{notif.message}</p>
                                {notif.data && Object.keys(notif.data).length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {Object.entries(notif.data).map(([key, value]) => (
                                      <span key={key} className="px-2 py-0.5 bg-zinc-800 text-[10px] text-zinc-400">
                                        {key}: {typeof value === 'number' ? value.toFixed(1) : value}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <p className="font-accent text-[9px] text-zinc-600 mt-2">
                                  {new Date(notif.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            {!notif.read && (
                              <button
                                onClick={() => markNotificationRead(notif.id)}
                                className="text-zinc-600 hover:text-green-500 transition-colors"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Military Scan View */}
          {activeView === "military" && (
            <div data-testid="military-view">
              <h2 className="font-display font-bold text-xl tracking-wider text-white mb-2">
                MILITARY GRADE SCAN
              </h2>
              <p className="text-zinc-500 text-sm mb-6">
                Multi-pass verification, cross-reference analysis. No Gemini (Google conflict).
              </p>
              
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">SELECT TARGET</span>
                </div>
                <div className="widget-content">
                  <p className="text-xs text-zinc-500 mb-4">Only YouTube channels can be scanned. Other platforms coming soon.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {channels.filter(ch => ch.platform === 'youtube').map((ch) => (
                      <button
                        key={ch.id}
                        onClick={() => runMilitaryScan(ch.id)}
                        disabled={scanInProgress}
                        className={`channel-card text-left ${selectedChannel === ch.id ? 'border-red-500' : ''}`}
                      >
                        <div className="channel-avatar bg-red-600">
                          <Youtube className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <p className="font-display font-semibold text-white">{ch.display_name || ch.handle}</p>
                          <p className="font-accent text-[10px] text-zinc-600">@{ch.handle} • YOUTUBE</p>
                        </div>
                        {scanInProgress && selectedChannel === ch.id && (
                          <Loader2 className="w-4 h-4 animate-spin text-red-500" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {militaryScanResult && (
                <div className="space-y-6">
                  {/* Main Score Widget */}
                  <div className="widget">
                    <div className="widget-header">
                      <span className="widget-title">SCAN RESULTS</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyToClipboard(formatReportForTTS(militaryScanResult, "scan"))}
                          className="flex items-center gap-1 px-3 py-1 bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs hover:bg-zinc-700 hover:text-white transition-colors"
                          data-testid="copy-scan-report-btn"
                        >
                          <Copy className="w-3 h-3" />
                          COPY REPORT
                        </button>
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border ${threatColors[militaryScanResult.threat_level]}`}>
                          {militaryScanResult.threat_level}
                        </span>
                      </div>
                    </div>
                    <div className="widget-content">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="stat-value text-2xl" style={{ color: militaryScanResult.visibility_score >= 50 ? '#00FF41' : '#FF2A2A' }}>
                            {militaryScanResult.visibility_score}/100
                          </div>
                          <div className="stat-label">VISIBILITY</div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="stat-value text-2xl text-cyan-400">
                            {militaryScanResult.confidence?.toFixed(1)}%
                          </div>
                          <div className="stat-label">CONFIDENCE</div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="stat-value text-2xl text-purple-400">
                            {militaryScanResult.youtube_proof?.views_to_subs_ratio?.toFixed(1) || 0}%
                          </div>
                          <div className="stat-label">REACH RATIO</div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="stat-value text-2xl text-yellow-400">
                            {militaryScanResult.youtube_proof?.engagement_rate?.toFixed(1) || 0}%
                          </div>
                          <div className="stat-label">ENGAGEMENT</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* YouTube API Proof - Concrete Evidence */}
                  {militaryScanResult.youtube_proof?.channel_found && (
                    <div className="widget">
                      <div className="widget-header">
                        <span className="widget-title">
                          <Database className="w-4 h-4 inline mr-2 text-green-500" />
                          YOUTUBE API PROOF - LIVE DATA
                        </span>
                        <span className="text-xs text-green-500 border border-green-500/30 px-2 py-0.5">VERIFIED</span>
                      </div>
                      <div className="widget-content">
                        {/* Channel Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                          <div className="bg-zinc-900/50 p-3 border border-zinc-800">
                            <div className="text-xl font-bold text-cyan-400">
                              {militaryScanResult.youtube_proof.subscribers?.toLocaleString()}
                            </div>
                            <div className="text-[10px] text-zinc-500">SUBSCRIBERS</div>
                          </div>
                          <div className="bg-zinc-900/50 p-3 border border-zinc-800">
                            <div className="text-xl font-bold text-green-400">
                              {militaryScanResult.youtube_proof.total_channel_views?.toLocaleString()}
                            </div>
                            <div className="text-[10px] text-zinc-500">TOTAL VIEWS</div>
                          </div>
                          <div className="bg-zinc-900/50 p-3 border border-zinc-800">
                            <div className="text-xl font-bold text-purple-400">
                              {militaryScanResult.youtube_proof.video_count}
                            </div>
                            <div className="text-[10px] text-zinc-500">VIDEOS</div>
                          </div>
                          <div className="bg-zinc-900/50 p-3 border border-zinc-800">
                            <div className="text-xl font-bold text-yellow-400">
                              {militaryScanResult.youtube_proof.recent_videos_avg_views?.toLocaleString()}
                            </div>
                            <div className="text-[10px] text-zinc-500">AVG VIEWS/VIDEO</div>
                          </div>
                        </div>
                        
                        {/* Algorithm Health Scores */}
                        <div className="mb-6">
                          <h4 className="text-xs text-zinc-500 mb-3 font-mono">ALGORITHM HEALTH SCORES</h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-zinc-900/50 p-4 border border-zinc-800">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-xs text-zinc-400">REACH</span>
                                <span className={`text-lg font-bold ${
                                  militaryScanResult.youtube_proof.algorithm_health_scores?.reach_score >= 70 ? 'text-green-500' :
                                  militaryScanResult.youtube_proof.algorithm_health_scores?.reach_score >= 40 ? 'text-yellow-500' : 'text-red-500'
                                }`}>
                                  {militaryScanResult.youtube_proof.algorithm_health_scores?.reach_score || 0}/100
                                </span>
                              </div>
                              <div className="w-full bg-zinc-800 h-2">
                                <div 
                                  className={`h-full transition-all ${
                                    militaryScanResult.youtube_proof.algorithm_health_scores?.reach_score >= 70 ? 'bg-green-500' :
                                    militaryScanResult.youtube_proof.algorithm_health_scores?.reach_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${militaryScanResult.youtube_proof.algorithm_health_scores?.reach_score || 0}%` }}
                                ></div>
                              </div>
                              <p className="text-[10px] text-zinc-600 mt-1">
                                {militaryScanResult.youtube_proof.views_to_subs_ratio?.toFixed(1)}% of subs view each video
                              </p>
                            </div>
                            <div className="bg-zinc-900/50 p-4 border border-zinc-800">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-xs text-zinc-400">ENGAGEMENT</span>
                                <span className={`text-lg font-bold ${
                                  militaryScanResult.youtube_proof.algorithm_health_scores?.engagement_score >= 70 ? 'text-green-500' :
                                  militaryScanResult.youtube_proof.algorithm_health_scores?.engagement_score >= 40 ? 'text-yellow-500' : 'text-red-500'
                                }`}>
                                  {militaryScanResult.youtube_proof.algorithm_health_scores?.engagement_score || 0}/100
                                </span>
                              </div>
                              <div className="w-full bg-zinc-800 h-2">
                                <div 
                                  className={`h-full transition-all ${
                                    militaryScanResult.youtube_proof.algorithm_health_scores?.engagement_score >= 70 ? 'bg-green-500' :
                                    militaryScanResult.youtube_proof.algorithm_health_scores?.engagement_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${militaryScanResult.youtube_proof.algorithm_health_scores?.engagement_score || 0}%` }}
                                ></div>
                              </div>
                              <p className="text-[10px] text-zinc-600 mt-1">
                                {militaryScanResult.youtube_proof.engagement_rate?.toFixed(2)}% ({militaryScanResult.youtube_proof.engagement_source === 'comments_only' ? 'comments only - likes N/A from API' : 'likes+comments'})
                              </p>
                            </div>
                            <div className="bg-zinc-900/50 p-4 border border-zinc-800">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-xs text-zinc-400">CONSISTENCY</span>
                                <span className={`text-lg font-bold ${
                                  militaryScanResult.youtube_proof.algorithm_health_scores?.consistency_score >= 70 ? 'text-green-500' :
                                  militaryScanResult.youtube_proof.algorithm_health_scores?.consistency_score >= 40 ? 'text-yellow-500' : 'text-red-500'
                                }`}>
                                  {militaryScanResult.youtube_proof.algorithm_health_scores?.consistency_score || 0}/100
                                </span>
                              </div>
                              <div className="w-full bg-zinc-800 h-2">
                                <div 
                                  className={`h-full transition-all ${
                                    militaryScanResult.youtube_proof.algorithm_health_scores?.consistency_score >= 70 ? 'bg-green-500' :
                                    militaryScanResult.youtube_proof.algorithm_health_scores?.consistency_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${militaryScanResult.youtube_proof.algorithm_health_scores?.consistency_score || 0}%` }}
                                ></div>
                              </div>
                              <p className="text-[10px] text-zinc-600 mt-1">
                                Upload every {militaryScanResult.youtube_proof.avg_days_between_uploads?.toFixed(0) || '?'} days avg
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Recent Video Performance Table */}
                        {militaryScanResult.youtube_proof.video_performance?.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-xs text-zinc-500 font-mono">RECENT VIDEO PERFORMANCE (Last {militaryScanResult.youtube_proof.video_performance.length} videos)</h4>
                              {!militaryScanResult.youtube_proof.likes_available_from_api && (
                                <span className="text-[10px] text-yellow-500 border border-yellow-500/30 px-2 py-0.5">
                                  ⚠️ Like data unavailable from YouTube API
                                </span>
                              )}
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-zinc-800">
                                    <th className="text-left p-2 text-zinc-500">VIDEO</th>
                                    <th className="text-right p-2 text-zinc-500">VIEWS</th>
                                    <th className="text-right p-2 text-zinc-500">COMMENTS</th>
                                    <th className="text-right p-2 text-zinc-500">ENGAGEMENT</th>
                                    <th className="text-right p-2 text-zinc-500">PUBLISHED</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {militaryScanResult.youtube_proof.video_performance.map((video, idx) => (
                                    <tr key={idx} className="border-b border-zinc-900 hover:bg-zinc-900/50">
                                      <td className="p-2 text-white truncate max-w-[200px]">{video.title}</td>
                                      <td className="p-2 text-right text-cyan-400">{video.views?.toLocaleString()}</td>
                                      <td className="p-2 text-right text-green-400">{video.comments}</td>
                                      <td className="p-2 text-right text-yellow-400">{video.engagement}</td>
                                      <td className="p-2 text-right text-zinc-500">{video.published}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* AI Analysis */}
                  {militaryScanResult.ai_analysis && (
                    <div className="widget">
                      <div className="widget-header">
                        <span className="widget-title">AI ANALYSIS</span>
                      </div>
                      <div className="widget-content">
                        <div className="ai-output text-sm whitespace-pre-wrap max-h-96 overflow-y-auto">
                          {militaryScanResult.ai_analysis}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Algorithm POV View */}
          {activeView === "algorithm" && (
            <div data-testid="algorithm-view">
              <h2 className="font-display font-bold text-xl tracking-wider text-white mb-2">
                ALGORITHM PERSPECTIVE
              </h2>
              <p className="text-zinc-500 text-sm mb-6">
                See what YouTube's algorithm sees. Understand WHY it makes decisions about your content.
              </p>
              
              {/* Channel Selection */}
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">SELECT CHANNEL</span>
                </div>
                <div className="widget-content">
                  <p className="text-xs text-zinc-500 mb-4">Choose a YouTube channel to analyze from the algorithm's perspective.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {channels.filter(ch => ch.platform === 'youtube').map((ch) => (
                      <button
                        key={ch.id}
                        onClick={() => {
                          setSelectedChannel(ch.id);
                          fetchAlgorithmPerspective(ch.id);
                        }}
                        disabled={loading}
                        className={`channel-card text-left ${selectedChannel === ch.id ? 'border-cyan-500' : ''}`}
                      >
                        <div className="channel-avatar bg-cyan-600">
                          <Eye className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <p className="font-display font-semibold text-white">{ch.display_name || ch.handle}</p>
                          <p className="font-accent text-[10px] text-zinc-600">@{ch.handle}</p>
                        </div>
                        {loading && selectedChannel === ch.id && (
                          <Loader2 className="w-4 h-4 animate-spin text-cyan-500" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Quick Actions */}
              {selectedChannel && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <button
                    onClick={() => fetchAlgorithmPerspective(selectedChannel)}
                    disabled={loading}
                    className="btn-primary flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    ALGORITHM'S VIEW
                  </button>
                  <button
                    onClick={() => fetchNicheIntelligence(selectedChannel)}
                    disabled={loading}
                    className="btn-primary flex items-center justify-center gap-2"
                  >
                    <Target className="w-4 h-4" />
                    DISCOVER NICHE TRICKS
                  </button>
                  <button
                    onClick={() => {
                      const youtubeChannels = channels.filter(ch => ch.platform === 'youtube');
                      if (youtubeChannels.length >= 2) {
                        compareChannelAlgorithms(youtubeChannels.map(ch => ch.id));
                      } else {
                        toast.error("Need at least 2 YouTube channels to compare");
                      }
                    }}
                    disabled={loading}
                    className="btn-primary flex items-center justify-center gap-2"
                  >
                    <GitCompare className="w-4 h-4" />
                    COMPARE CHANNELS
                  </button>
                </div>
              )}
              
              {/* Loading State */}
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
                  <span className="ml-3 text-zinc-400 font-mono text-sm">Analyzing from algorithm's perspective...</span>
                </div>
              )}
              
              {/* Algorithm Perspective Results */}
              {algorithmPerspective && !loading && (
                <div className="space-y-6">
                  {/* Classification Header */}
                  <div className="widget">
                    <div className="widget-header">
                      <span className="widget-title">
                        <Eye className="w-4 h-4 inline mr-2 text-cyan-500" />
                        ALGORITHM CLASSIFICATION
                      </span>
                      <button
                        onClick={() => copyToClipboard(`ALGORITHM PERSPECTIVE REPORT - @${algorithmPerspective.handle}\n\nNiche: ${algorithmPerspective.detected_niche}\nClassification Confidence: ${algorithmPerspective.algorithm_classification?.confidence}%\n\n${algorithmPerspective.algorithm_perspective}\n\n— TSVVictoryTwister Algorithm Intel`)}
                        className="flex items-center gap-1 px-3 py-1 bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs hover:bg-zinc-700 hover:text-white transition-colors"
                      >
                        <Copy className="w-3 h-3" />
                        COPY REPORT
                      </button>
                    </div>
                    <div className="widget-content">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="text-xl font-bold text-cyan-400 uppercase">
                            {algorithmPerspective.detected_niche}
                          </div>
                          <div className="stat-label">DETECTED NICHE</div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="stat-value text-2xl text-green-400">
                            {algorithmPerspective.algorithm_classification?.confidence}%
                          </div>
                          <div className="stat-label">CONFIDENCE</div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="stat-value text-xl text-purple-400">
                            {algorithmPerspective.algorithm_metrics?.views_to_subs_ratio}%
                          </div>
                          <div className="stat-label">REACH RATIO</div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="stat-value text-xl text-yellow-400">
                            {algorithmPerspective.algorithm_metrics?.engagement_rate}%
                          </div>
                          <div className="stat-label">ENGAGEMENT</div>
                        </div>
                      </div>
                      
                      {/* Audience Targeting */}
                      <div className="bg-zinc-900/50 p-4 border border-zinc-800 mb-4">
                        <h4 className="text-xs text-zinc-500 mb-3 font-mono">ALGORITHM'S AUDIENCE TARGETING</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-zinc-600">Age Range:</span>
                            <span className="ml-2 text-cyan-400">{algorithmPerspective.algorithm_classification?.audience_signals?.likely_age_range}</span>
                          </div>
                          <div>
                            <span className="text-zinc-600">Gender:</span>
                            <span className="ml-2 text-purple-400">{algorithmPerspective.algorithm_classification?.audience_signals?.likely_gender}</span>
                          </div>
                          <div>
                            <span className="text-zinc-600">Interests:</span>
                            <span className="ml-2 text-yellow-400">{algorithmPerspective.algorithm_classification?.audience_signals?.likely_interests?.join(", ")}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Keywords */}
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs text-zinc-600">Keywords detected:</span>
                        {algorithmPerspective.algorithm_classification?.keywords_detected?.slice(0, 8).map((kw, idx) => (
                          <span key={idx} className="px-2 py-1 bg-zinc-800 border border-zinc-700 text-xs text-zinc-400">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Algorithm's Voice - Dual AI Perspectives */}
                  <div className="widget">
                    <div className="widget-header">
                      <span className="widget-title">THE ALGORITHM SPEAKS</span>
                      <span className="text-xs text-cyan-500 border border-cyan-500/30 px-2 py-0.5">DUAL AI ANALYSIS</span>
                    </div>
                    <div className="widget-content">
                      {algorithmPerspective.gpt_perspective && algorithmPerspective.claude_perspective ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="border border-green-500/20">
                            <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border-b border-green-500/20">
                              <div className="w-2 h-2 rounded-full bg-green-500" />
                              <span className="text-xs font-mono text-green-400">GPT-5.2 PERSPECTIVE</span>
                            </div>
                            <div className="p-3 ai-output text-sm whitespace-pre-wrap max-h-[500px] overflow-y-auto leading-relaxed">
                              {algorithmPerspective.gpt_perspective}
                            </div>
                          </div>
                          <div className="border border-orange-500/20">
                            <div className="flex items-center gap-2 px-3 py-2 bg-orange-500/10 border-b border-orange-500/20">
                              <div className="w-2 h-2 rounded-full bg-orange-500" />
                              <span className="text-xs font-mono text-orange-400">CLAUDE PERSPECTIVE</span>
                            </div>
                            <div className="p-3 ai-output text-sm whitespace-pre-wrap max-h-[500px] overflow-y-auto leading-relaxed">
                              {algorithmPerspective.claude_perspective}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="ai-output text-sm whitespace-pre-wrap max-h-[600px] overflow-y-auto leading-relaxed">
                          {algorithmPerspective.algorithm_perspective}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Niche Intelligence Results */}
              {nicheIntel && !loading && (
                <div className="space-y-6 mt-6">
                  {/* Header with Niche Info */}
                  <div className="widget">
                    <div className="widget-header">
                      <span className="widget-title">
                        <Target className="w-4 h-4 inline mr-2 text-green-500" />
                        NICHE INTELLIGENCE - 2026 TACTICS
                      </span>
                      <div className="flex items-center gap-2">
                        {nicheIntel.from_cache && (
                          <span className="text-xs text-yellow-400 border border-yellow-500/30 px-2 py-0.5 flex items-center gap-1">
                            <Database className="w-3 h-3" />
                            CACHED ({nicheIntel.cache_age_hours}h ago)
                          </span>
                        )}
                        <span className="text-xs text-purple-400 border border-purple-500/30 px-2 py-0.5 uppercase">
                          {nicheIntel.detected_niche || "GAMING/STORYTELLING"}
                        </span>
                        <button
                          onClick={() => fetchNicheIntelligence(selectedChannel, true)}
                          disabled={loading}
                          className="text-xs text-cyan-400 border border-cyan-500/30 px-2 py-0.5 hover:bg-cyan-500/10 transition-colors flex items-center gap-1"
                          title="Generate fresh AI analysis (uses LLM budget)"
                        >
                          <RefreshCw className="w-3 h-3" />
                          REFRESH
                        </button>
                      </div>
                    </div>
                    <div className="widget-content">
                      {/* Cache Notice */}
                      {nicheIntel.from_cache && (
                        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-300 flex items-center gap-2">
                          <Database className="w-4 h-4" />
                          <span>Using cached analysis from {nicheIntel.cache_age_hours} hours ago. Click REFRESH to generate new AI analysis (uses LLM budget).</span>
                        </div>
                      )}
                      
                      {/* Trending Tactics Grid */}
                      {nicheIntel.trending_tactics && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          {/* Hot Formats */}
                          <div className="bg-zinc-900/50 p-4 border border-red-500/30">
                            <h4 className="text-xs text-red-400 font-mono mb-3 flex items-center gap-2">
                              <Zap className="w-3 h-3" /> HOT FORMATS IN 2026
                            </h4>
                            <ul className="space-y-2">
                              {nicheIntel.trending_tactics.hot_formats?.map((format, idx) => (
                                <li key={idx} className="text-xs text-zinc-300 flex items-start gap-2">
                                  <span className="text-green-500 mt-0.5">▸</span>
                                  {format}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          {/* Title Patterns */}
                          <div className="bg-zinc-900/50 p-4 border border-cyan-500/30">
                            <h4 className="text-xs text-cyan-400 font-mono mb-3 flex items-center gap-2">
                              <FileText className="w-3 h-3" /> TITLE PATTERNS THAT WORK
                            </h4>
                            <ul className="space-y-2">
                              {nicheIntel.trending_tactics.title_patterns?.map((pattern, idx) => (
                                <li key={idx} className="text-xs text-zinc-300 font-mono bg-black/30 p-2 border-l-2 border-cyan-500">
                                  {pattern}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          {/* Thumbnail Trends */}
                          <div className="bg-zinc-900/50 p-4 border border-purple-500/30">
                            <h4 className="text-xs text-purple-400 font-mono mb-3 flex items-center gap-2">
                              <Eye className="w-3 h-3" /> THUMBNAIL TRENDS
                            </h4>
                            <ul className="space-y-2">
                              {nicheIntel.trending_tactics.thumbnail_trends?.map((trend, idx) => (
                                <li key={idx} className="text-xs text-zinc-300 flex items-start gap-2">
                                  <span className="text-purple-500 mt-0.5">◆</span>
                                  {trend}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          {/* Upload Windows */}
                          <div className="bg-zinc-900/50 p-4 border border-yellow-500/30">
                            <h4 className="text-xs text-yellow-400 font-mono mb-3 flex items-center gap-2">
                              <Clock className="w-3 h-3" /> OPTIMAL UPLOAD WINDOWS
                            </h4>
                            <ul className="space-y-2">
                              {nicheIntel.trending_tactics.upload_windows?.map((window, idx) => (
                                <li key={idx} className="text-xs text-zinc-300 flex items-start gap-2">
                                  <span className="text-yellow-500 mt-0.5">⏱</span>
                                  {window}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                      
                      {/* Growth Hacks Quick List */}
                      {nicheIntel.trending_tactics?.growth_hacks && (
                        <div className="mb-6 p-4 bg-gradient-to-r from-green-500/10 to-cyan-500/10 border border-green-500/30">
                          <h4 className="text-xs text-green-400 font-mono mb-3">⚡ QUICK GROWTH HACKS</h4>
                          <div className="flex flex-wrap gap-2">
                            {nicheIntel.trending_tactics.growth_hacks.map((hack, idx) => (
                              <span key={idx} className="text-xs bg-zinc-900 text-zinc-300 px-3 py-1 border border-zinc-700">
                                {hack}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Projected Growth */}
                      {nicheIntel.trending_tactics?.projected_growth && (
                        <div className="mb-6 p-3 bg-zinc-900 border border-zinc-700 flex items-center justify-between">
                          <span className="text-xs text-zinc-500 font-mono">PROJECTED NICHE GROWTH:</span>
                          <span className="text-sm text-green-400 font-bold">{nicheIntel.trending_tactics.projected_growth}</span>
                        </div>
                      )}
                      
                      {/* Full AI Analysis */}
                      <div className="widget">
                        <div className="widget-header">
                          <span className="widget-title">
                            <Brain className="w-4 h-4 inline mr-2 text-red-500" />
                            FULL INTELLIGENCE REPORT
                          </span>
                          <button
                            onClick={() => copyToClipboard(`NICHE INTELLIGENCE REPORT - @${nicheIntel.handle}\n\nNiche: ${nicheIntel.detected_niche}\n\n${nicheIntel.tricks_discovered}\n\n— TSVVictoryTwister 2026`)}
                            className="flex items-center gap-1 px-3 py-1 bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs hover:bg-zinc-700 hover:text-white transition-colors"
                          >
                            <Copy className="w-3 h-3" />
                            COPY REPORT
                          </button>
                        </div>
                        <div className="widget-content">
                          <div className="ai-output text-sm whitespace-pre-wrap max-h-[600px] overflow-y-auto leading-relaxed">
                            {nicheIntel.tricks_discovered}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Algorithm Channel Comparison */}
              {algorithmComparison && !loading && (
                <div className="space-y-6 mt-6">
                  <div className="widget">
                    <div className="widget-header">
                      <span className="widget-title">
                        <GitCompare className="w-4 h-4 inline mr-2 text-purple-500" />
                        ALGORITHM COMPARISON - {algorithmComparison.channels_compared} CHANNELS
                      </span>
                    </div>
                    <div className="widget-content">
                      {/* Comparison Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        {algorithmComparison.comparisons?.map((comp, idx) => (
                          <div key={idx} className="bg-zinc-900/50 p-4 border border-zinc-800">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <p className="font-semibold text-white">@{comp.handle}</p>
                                <p className="text-xs text-purple-400">{comp.detected_niche}</p>
                              </div>
                              <span className="text-xs px-2 py-1 bg-purple-500/10 border border-purple-500/30 text-purple-400">
                                {comp.algorithm_signals?.recommendation_potential}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                              <div>
                                <span className="text-zinc-600">Subs:</span>
                                <span className="ml-1 text-cyan-400">{comp.metrics?.subscribers?.toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-zinc-600">Avg Views:</span>
                                <span className="ml-1 text-green-400">{comp.metrics?.avg_views?.toLocaleString()}</span>
                              </div>
                              <div>
                                <span className="text-zinc-600">Reach:</span>
                                <span className="ml-1 text-purple-400">{comp.metrics?.views_to_subs_ratio}%</span>
                              </div>
                              <div>
                                <span className="text-zinc-600">Engagement:</span>
                                <span className="ml-1 text-yellow-400">{comp.metrics?.engagement_rate}%</span>
                              </div>
                            </div>
                            <div className="text-[10px] text-zinc-600">
                              <p>Audience: {comp.algorithm_signals?.audience_targeting}</p>
                              <p>Search: {comp.algorithm_signals?.search_discoverability}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Comparison Analysis */}
                      <div className="ai-output text-sm whitespace-pre-wrap max-h-[500px] overflow-y-auto leading-relaxed">
                        {algorithmComparison.algorithm_comparison}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Thumbnail Analysis Section */}
              <div className="widget mt-6" data-testid="thumbnail-analysis-section">
                <div className="widget-header">
                  <span className="widget-title">THUMBNAIL / IMAGE ANALYSIS</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        setAbMode(!abMode);
                        setAbSelections([null, null]);
                        setAbResult(null);
                      }}
                      data-testid="ab-mode-toggle"
                      className={`px-3 py-1 text-xs font-mono border transition-all ${
                        abMode
                          ? "border-yellow-500 bg-yellow-500/20 text-yellow-300"
                          : "border-zinc-700 text-zinc-500 hover:border-zinc-500"
                      }`}
                    >
                      {abMode ? "A/B MODE ON" : "A/B COMPARE"}
                    </button>
                    <span className="text-xs text-zinc-500">AI Vision</span>
                  </div>
                </div>
                <div className="widget-content">
                  <p className="text-xs text-zinc-500 mb-4">
                    {abMode 
                      ? "A/B MODE: Select two thumbnails to compare head-to-head. The algorithm decides the winner."
                      : "Select a channel to auto-load thumbnails, or paste a direct URL below."
                    }
                  </p>
                  
                  {/* Channel selector for thumbnails */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {channels.map((ch) => (
                      <button
                        key={ch.id}
                        onClick={() => fetchChannelThumbnails(ch.id)}
                        disabled={thumbnailsLoading}
                        data-testid={`thumb-channel-${ch.id}`}
                        className={`px-3 py-1.5 text-xs border transition-all ${
                          channelThumbnails?.channel_id === ch.id
                            ? "border-cyan-500 bg-cyan-500/20 text-cyan-300"
                            : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                        }`}
                      >
                        {ch.platform === 'instagram' ? <Instagram className="w-3 h-3 inline mr-1" /> : <Youtube className="w-3 h-3 inline mr-1" />}
                        {ch.handle || ch.name}
                      </button>
                    ))}
                  </div>
                  
                  {/* Loading state */}
                  {thumbnailsLoading && (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-zinc-500">Loading thumbnails...</p>
                    </div>
                  )}
                  
                  {/* Thumbnail Grid */}
                  {channelThumbnails && channelThumbnails.thumbnails?.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs text-zinc-400">
                          {channelThumbnails.count} recent {channelThumbnails.platform === 'youtube' ? 'videos' : 'posts'} from {channelThumbnails.channel_name}
                        </span>
                        <span className="text-xs text-zinc-600">
                          {abMode ? "Pick 2 thumbnails to A/B test" : "Click any thumbnail to analyze"}
                        </span>
                      </div>
                      
                      {/* A/B Selection Bar */}
                      {abMode && (
                        <div className="flex items-center gap-4 p-3 mb-4 bg-zinc-900 border border-yellow-500/30" data-testid="ab-selection-bar">
                          <div className="flex-1 flex items-center gap-3">
                            <div className={`flex items-center gap-2 px-3 py-2 border ${abSelections[0] ? 'border-cyan-500 bg-cyan-500/10' : 'border-zinc-700 border-dashed'}`}>
                              {abSelections[0] ? (
                                <>
                                  <img src={abSelections[0].thumbnail_url} alt="A" className="w-12 h-8 object-cover" />
                                  <div>
                                    <span className="text-xs text-cyan-400 font-bold">A</span>
                                    <p className="text-[10px] text-zinc-400 max-w-[120px] truncate">{abSelections[0].title}</p>
                                  </div>
                                </>
                              ) : (
                                <span className="text-xs text-zinc-600">Select Thumbnail A</span>
                              )}
                            </div>
                            <span className="text-yellow-400 font-bold text-sm">VS</span>
                            <div className={`flex items-center gap-2 px-3 py-2 border ${abSelections[1] ? 'border-pink-500 bg-pink-500/10' : 'border-zinc-700 border-dashed'}`}>
                              {abSelections[1] ? (
                                <>
                                  <img src={abSelections[1].thumbnail_url} alt="B" className="w-12 h-8 object-cover" />
                                  <div>
                                    <span className="text-xs text-pink-400 font-bold">B</span>
                                    <p className="text-[10px] text-zinc-400 max-w-[120px] truncate">{abSelections[1].title}</p>
                                  </div>
                                </>
                              ) : (
                                <span className="text-xs text-zinc-600">Select Thumbnail B</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setAbSelections([null, null]); setAbResult(null); }}
                              className="px-3 py-1.5 text-xs border border-zinc-700 text-zinc-400 hover:text-white"
                            >
                              RESET
                            </button>
                            <button
                              onClick={runAbComparison}
                              disabled={!abSelections[0] || !abSelections[1] || abLoading}
                              data-testid="ab-compare-btn"
                              className={`px-4 py-1.5 text-xs font-bold transition-all ${
                                abSelections[0] && abSelections[1] && !abLoading
                                  ? "bg-gradient-to-r from-yellow-600 to-orange-600 text-black hover:from-yellow-500 hover:to-orange-500"
                                  : "bg-zinc-800 text-zinc-600 cursor-not-allowed"
                              }`}
                            >
                              {abLoading ? <><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> COMPARING...</> : "COMPARE"}
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {channelThumbnails.thumbnails.map((thumb) => {
                          const isSelectedA = abMode && abSelections[0]?.id === thumb.id;
                          const isSelectedB = abMode && abSelections[1]?.id === thumb.id;
                          return (
                          <div
                            key={thumb.id}
                            data-testid={`thumbnail-card-${thumb.id}`}
                            onClick={() => handleThumbnailClick(thumb)}
                            className={`group cursor-pointer border transition-all hover:scale-[1.02] ${
                              isSelectedA
                                ? "border-cyan-500 ring-2 ring-cyan-500/50"
                                : isSelectedB
                                  ? "border-pink-500 ring-2 ring-pink-500/50"
                                  : analyzingThumbId === thumb.id
                                    ? "border-yellow-500 ring-1 ring-yellow-500/50"
                                    : thumbnailAnalysis?.thumbnail_url === thumb.thumbnail_url
                                      ? "border-green-500 ring-1 ring-green-500/50"
                                      : "border-zinc-800 hover:border-cyan-500/50"
                            }`}
                          >
                            <div className="relative aspect-video bg-zinc-900 overflow-hidden">
                              {thumb.thumbnail_url ? (
                                <img
                                  src={thumb.thumbnail_url}
                                  alt={thumb.title}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-zinc-700">
                                  <ImageIcon className="w-8 h-8" />
                                </div>
                              )}
                              {/* A/B label badges */}
                              {isSelectedA && (
                                <div className="absolute top-1 left-1 bg-cyan-500 text-black text-xs font-bold px-2 py-0.5">A</div>
                              )}
                              {isSelectedB && (
                                <div className="absolute top-1 left-1 bg-pink-500 text-black text-xs font-bold px-2 py-0.5">B</div>
                              )}
                              {/* Overlay on hover */}
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                {analyzingThumbId === thumb.id ? (
                                  <Loader2 className="w-6 h-6 text-yellow-400 animate-spin" />
                                ) : (
                                  <Eye className="w-6 h-6 text-cyan-400" />
                                )}
                              </div>
                              {/* Analyzed badge */}
                              {!abMode && thumbnailAnalysis?.thumbnail_url === thumb.thumbnail_url && (
                                <div className="absolute top-1 right-1 bg-green-500 text-black text-[10px] font-bold px-1.5 py-0.5">
                                  ANALYZED
                                </div>
                              )}
                            </div>
                            <div className="p-2 bg-zinc-900">
                              <p className="text-xs text-zinc-300 truncate">{thumb.title || "Untitled"}</p>
                              <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-600">
                                {thumb.views !== undefined && <span>{thumb.views?.toLocaleString()} views</span>}
                                <span>{thumb.likes?.toLocaleString()} likes</span>
                                {thumb.published_at && (
                                  <span>{new Date(thumb.published_at).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Manual URL input (fallback) */}
                  <div className="border-t border-zinc-800 pt-4 mt-2">
                    <p className="text-xs text-zinc-600 mb-2">Or paste a direct URL:</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={thumbnailUrl}
                        onChange={(e) => setThumbnailUrl(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=... or image URL"
                        className="flex-1 bg-zinc-900 border border-zinc-800 text-white px-3 py-2 text-sm focus:outline-none focus:border-cyan-500"
                        data-testid="thumbnail-url-input"
                      />
                      <button
                        onClick={() => analyzeThumbnail(thumbnailUrl, selectedChannel)}
                        disabled={loading || !thumbnailUrl}
                        className="btn-primary"
                        data-testid="thumbnail-analyze-btn"
                      >
                        ANALYZE
                      </button>
                    </div>
                  </div>
                  
                  {/* A/B Comparison Result */}
                  {abResult && (
                    <div className="mt-6 border-t border-yellow-500/30 pt-4" data-testid="ab-comparison-result">
                      <h4 className="text-sm font-mono text-yellow-400 mb-4">A/B THUMBNAIL SHOWDOWN</h4>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="border border-cyan-500/30 p-3 bg-zinc-900">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="bg-cyan-500 text-black text-xs font-bold px-2 py-0.5">A</span>
                            <span className="text-xs text-zinc-300 truncate">{abResult.thumbnail_a?.title || "Untitled"}</span>
                          </div>
                          {abResult.thumbnail_a?.url && (
                            <img src={abResult.thumbnail_a.url} alt="Thumbnail A" className="w-full border border-zinc-800" />
                          )}
                        </div>
                        <div className="border border-pink-500/30 p-3 bg-zinc-900">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="bg-pink-500 text-black text-xs font-bold px-2 py-0.5">B</span>
                            <span className="text-xs text-zinc-300 truncate">{abResult.thumbnail_b?.title || "Untitled"}</span>
                          </div>
                          {abResult.thumbnail_b?.url && (
                            <img src={abResult.thumbnail_b.url} alt="Thumbnail B" className="w-full border border-zinc-800" />
                          )}
                        </div>
                      </div>
                      <div className="ai-output text-sm whitespace-pre-wrap max-h-[600px] overflow-y-auto leading-relaxed" data-testid="ab-comparison-text">
                        {abResult.comparison}
                      </div>
                    </div>
                  )}
                  
                  {/* Analysis Result */}
                  {thumbnailAnalysis && (
                    <div className="mt-6 border-t border-zinc-800 pt-4" data-testid="thumbnail-analysis-result">
                      <div className="flex items-center gap-3 mb-4">
                        <h4 className="text-sm font-mono text-cyan-400">ALGORITHM VISION ANALYSIS</h4>
                        {thumbnailAnalysis.platform && (
                          <span className={`text-[10px] px-2 py-0.5 border ${
                            thumbnailAnalysis.platform === 'instagram' ? 'border-pink-500/30 text-pink-400' : 'border-red-500/30 text-red-400'
                          }`}>
                            {thumbnailAnalysis.platform.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-4">
                        {thumbnailAnalysis.thumbnail_url && (
                          <div className="flex-shrink-0">
                            <img
                              src={thumbnailAnalysis.thumbnail_url}
                              alt="Analyzed"
                              className="w-48 border border-zinc-800"
                              data-testid="analyzed-thumbnail-image"
                            />
                            {thumbnailAnalysis.video_title && (
                              <p className="text-xs text-zinc-500 mt-1 max-w-[192px] truncate">{thumbnailAnalysis.video_title}</p>
                            )}
                          </div>
                        )}
                        <div className="flex-1 ai-output text-sm whitespace-pre-wrap max-h-[500px] overflow-y-auto leading-relaxed">
                          {thumbnailAnalysis.analysis}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Root Cause View */}
          {activeView === "rootcause" && (
            <div data-testid="rootcause-view">
              <h2 className="font-display font-bold text-xl tracking-wider text-white mb-6">
                ROOT CAUSE ANALYSIS
              </h2>
              
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">SELECT CHANNEL</span>
                </div>
                <div className="widget-content">
                  <div className="flex flex-wrap gap-2">
                    {channels.map((ch) => (
                      <button
                        key={ch.id}
                        onClick={() => { setSelectedChannel(ch.id); fetchRootCause(ch.id); }}
                        className={`px-4 py-2 border text-xs font-mono transition-colors ${selectedChannel === ch.id ? 'border-red-500 bg-red-500/10 text-red-500' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                      >
                        @{ch.handle}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-red-500" />
                  <span className="ml-3 text-zinc-400 font-mono text-sm">Analyzing channel data...</span>
                </div>
              )}
              
              {rootCause && !loading && (
                <>
                  {/* Status Overview */}
                  <div className="widget mb-6">
                    <div className="widget-header">
                      <span className="widget-title">CURRENT STATUS</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => fetchRootCause(selectedChannel)}
                          className="flex items-center gap-1 px-3 py-1 bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs hover:bg-zinc-700 hover:text-white transition-colors"
                        >
                          <RefreshCw className="w-3 h-3" />
                          REFRESH
                        </button>
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border ${threatColors[rootCause.threat_level] || threatColors.unknown}`}>
                          {rootCause.threat_level}
                        </span>
                      </div>
                    </div>
                    <div className="widget-content">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="stat-value text-2xl" style={{ color: (rootCause.current_status?.visibility_score || rootCause.visibility_score || 0) >= 60 ? '#00FF41' : '#FF2A2A' }}>
                            {rootCause.current_status?.visibility_score || rootCause.visibility_score || 'N/A'}/100
                          </div>
                          <div className="stat-label">VISIBILITY SCORE</div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="stat-value text-2xl text-cyan-400">
                            {rootCause.current_status?.confidence || rootCause.confidence || 0}%
                          </div>
                          <div className="stat-label">CONFIDENCE</div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="flex items-center gap-2">
                            <TrendIcon trend={rootCause.trend_analysis?.trend} />
                            <span className={`stat-value text-xl ${
                              rootCause.trend_analysis?.trend === 'IMPROVING' ? 'text-green-500' :
                              rootCause.trend_analysis?.trend === 'DECLINING' ? 'text-red-500' : 'text-zinc-500'
                            }`}>
                              {rootCause.trend_analysis?.trend || 'N/A'}
                            </span>
                          </div>
                          <div className="stat-label">30-DAY TREND</div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className={`stat-value text-2xl ${(rootCause.trend_analysis?.score_change || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {(rootCause.trend_analysis?.score_change || 0) >= 0 ? '+' : ''}{rootCause.trend_analysis?.score_change?.toFixed(1) || 0}
                          </div>
                          <div className="stat-label">SCORE CHANGE</div>
                        </div>
                      </div>
                      
                      {/* Test Summary */}
                      {rootCause.test_summary && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          <span className="text-xs text-zinc-500 mr-2">Tests:</span>
                          <span className="px-2 py-1 bg-green-500/10 border border-green-500/30 text-green-500 text-xs">
                            {rootCause.test_summary.passed} PASSED
                          </span>
                          <span className="px-2 py-1 bg-red-500/10 border border-red-500/30 text-red-500 text-xs">
                            {rootCause.test_summary.failed} FAILED
                          </span>
                        </div>
                      )}
                      
                      {/* YouTube Metrics if available */}
                      {rootCause.youtube_metrics && (
                        <div className="mt-4 pt-4 border-t border-zinc-800">
                          <span className="text-xs text-zinc-500 block mb-2">YouTube Metrics:</span>
                          <div className="flex flex-wrap gap-4 text-xs">
                            <span className="text-cyan-400">
                              <Users className="w-3 h-3 inline mr-1" />
                              {rootCause.youtube_metrics.subscribers?.toLocaleString() || 'N/A'} subscribers
                            </span>
                            <span className="text-green-400">
                              <Eye className="w-3 h-3 inline mr-1" />
                              {rootCause.youtube_metrics.total_views?.toLocaleString() || 'N/A'} views
                            </span>
                            <span className="text-purple-400">
                              <Video className="w-3 h-3 inline mr-1" />
                              {rootCause.youtube_metrics.video_count || 'N/A'} videos
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Deep Analysis */}
                  <div className="widget">
                    <div className="widget-header">
                      <span className="widget-title">DEEP ANALYSIS</span>
                      <button
                        onClick={() => copyToClipboard(`TSV VICTORY TWISTER - ROOT CAUSE ANALYSIS REPORT

Channel: @${rootCause.handle || 'Unknown'}
Generated: ${new Date(rootCause.generated_at || rootCause.timestamp).toLocaleString()}

CURRENT STATUS
Threat Level: ${rootCause.threat_level}
Visibility Score: ${rootCause.current_status?.visibility_score || rootCause.visibility_score || 'N/A'}/100
30-Day Trend: ${rootCause.trend_analysis?.trend || 'N/A'} (${rootCause.trend_analysis?.score_change >= 0 ? '+' : ''}${rootCause.trend_analysis?.score_change?.toFixed(1) || 0} points)

ANALYSIS
${rootCause.analysis}

— Victoria Black | TSVVictoryTwister Military Intel`)}
                        className="flex items-center gap-1 px-3 py-1 bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs hover:bg-zinc-700 hover:text-white transition-colors"
                        data-testid="copy-rootcause-btn"
                      >
                        <Copy className="w-3 h-3" />
                        COPY REPORT
                      </button>
                    </div>
                    <div className="widget-content">
                      <div className="ai-output text-sm whitespace-pre-wrap max-h-[600px] overflow-y-auto leading-relaxed">
                        {rootCause.analysis}
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {!rootCause && !loading && selectedChannel && (
                <div className="widget">
                  <div className="widget-content text-center py-12">
                    <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-400 mb-4">No root cause analysis available yet.</p>
                    <p className="text-zinc-600 text-sm">Run a Military Scan first to gather data for analysis.</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Comparison View */}
          {activeView === "compare" && (
            <div data-testid="compare-view">
              <h2 className="font-display font-bold text-xl tracking-wider text-white mb-6">
                CHANNEL COMPARISON
              </h2>
              
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">1. SELECT YOUR CHANNEL</span>
                </div>
                <div className="widget-content">
                  <div className="flex flex-wrap gap-2">
                    {channels.map((ch) => (
                      <button
                        key={ch.id}
                        onClick={() => setSelectedChannel(ch.id)}
                        className={`px-4 py-2 border text-xs font-mono transition-colors ${selectedChannel === ch.id ? 'border-red-500 bg-red-500/10 text-red-500' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                      >
                        @{ch.handle}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">2. ADD COMPETITORS</span>
                </div>
                <div className="widget-content">
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      className="flex-1 bg-black border border-zinc-800 p-2 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-red-500 focus:outline-none"
                      placeholder="Enter competitor handle (e.g., MrBeast)"
                      value={competitorInput}
                      onChange={(e) => setCompetitorInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addCompetitor()}
                    />
                    <button onClick={addCompetitor} className="btn-secondary">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {competitors.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {competitors.map((comp, i) => (
                        <span key={i} className="px-3 py-1 bg-zinc-900 border border-zinc-700 text-xs text-zinc-300 flex items-center gap-2">
                          @{comp}
                          <button onClick={() => setCompetitors(competitors.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-500">
                            <XCircle className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="widget mb-6">
                <div className="widget-content">
                  <button
                    onClick={runComparison}
                    disabled={loading || !selectedChannel || competitors.length === 0}
                    className="btn-attack w-full"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <GitCompare className="w-4 h-4 inline mr-2" />}
                    RUN COMPARISON
                  </button>
                </div>
              </div>
              
              {comparisonResult && (
                <div className="widget">
                  <div className="widget-header">
                    <span className="widget-title">COMPARISON RESULTS</span>
                  </div>
                  <div className="widget-content">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-zinc-900 border border-red-500/30">
                        <span className="font-mono text-xs text-red-400">YOUR CHANNEL: @{comparisonResult.target}</span>
                        <span className="font-bold" style={{ color: comparisonResult.results?.target?.aggregate_score >= 60 ? '#00FF41' : '#FF2A2A' }}>
                          {comparisonResult.results?.target?.aggregate_score}/100
                        </span>
                      </div>
                      {comparisonResult.results?.competitors?.map((comp, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-zinc-900 border border-zinc-800">
                          <span className="font-mono text-xs text-zinc-400">@{comp.handle}</span>
                          <span className="font-bold" style={{ color: comp.aggregate_score >= 60 ? '#00FF41' : '#FF2A2A' }}>
                            {comp.aggregate_score}/100
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Channels View */}
          {activeView === "channels" && (
            <div data-testid="channels-view">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display font-bold text-xl tracking-wider text-white">TARGET MANAGEMENT</h2>
                <button onClick={() => setShowAddChannel(true)} className="btn-attack">
                  <Plus className="w-4 h-4 mr-2 inline" /> ADD TARGET
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {channels.map((ch) => (
                  <div key={ch.id} className="widget">
                    <div className="widget-content">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="channel-avatar w-16 h-16 text-2xl">{ch.handle?.charAt(0)?.toUpperCase() || "?"}</div>
                        <div>
                          <h3 className="font-display font-semibold text-white">{ch.display_name || ch.handle}</h3>
                          <p className="font-accent text-[10px] text-zinc-600">@{ch.handle}</p>
                          <p className="font-accent text-[10px] text-cyan-400 mt-1">{ch.platform?.toUpperCase()}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => runMilitaryScan(ch.id)} disabled={scanInProgress} className="btn-attack flex-1 text-xs">
                          {scanInProgress && selectedChannel === ch.id ? <Loader2 className="w-3 h-3 animate-spin inline mr-2" /> : <Radar className="w-3 h-3 inline mr-2" />}
                          SCAN
                        </button>
                        <button onClick={() => deleteChannel(ch.id)} className="btn-secondary px-3">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* AI Console View */}
          {activeView === "ai" && (
            <div data-testid="ai-view">
              <h2 className="font-display font-bold text-xl tracking-wider text-white mb-6">
                AI ANALYSIS CONSOLE
              </h2>
              
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">COMMAND INPUT</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors">
                        MODEL: {aiModel.toUpperCase()}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-zinc-900 border-zinc-800">
                      <DropdownMenuItem onClick={() => setAiModel("claude")} className="font-mono text-xs">CLAUDE 4.6</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setAiModel("gpt")} className="font-mono text-xs">GPT 5.2</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="widget-content">
                  <textarea
                    className="w-full h-32 bg-black border border-zinc-800 p-4 font-mono text-sm text-green-500 placeholder:text-zinc-600 focus:border-red-500 focus:outline-none resize-none"
                    placeholder="Enter analysis prompt..."
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                  />
                  <div className="flex justify-end mt-4">
                    <button onClick={runAIAnalysis} disabled={loading || !aiPrompt.trim()} className="btn-attack">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <Brain className="w-4 h-4 inline mr-2" />}
                      ANALYZE
                    </button>
                  </div>
                </div>
              </div>
              
              {aiResponse && (
                <div className="widget">
                  <div className="widget-header">
                    <span className="widget-title">AI RESPONSE</span>
                  </div>
                  <div className="widget-content">
                    <div className="ai-output whitespace-pre-wrap">{aiResponse}</div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Learn Algo Page */}
          {activeView === "learnalgo" && (
            <LearnAlgoEngine channels={channels} API={API} />
          )}

          {/* Global Scan View */}
          {activeView === "globalscan" && (
            <GlobalScan channels={channels} API={API} />
          )}
          
          {/* YouTube Analytics View */}
          {activeView === "youtube" && (
            <div data-testid="youtube-view">
              <h2 className="font-display font-bold text-xl tracking-wider text-white mb-2">
                YOUTUBE ANALYTICS
              </h2>
              <p className="text-zinc-500 text-sm mb-6">
                Real-time channel metrics from YouTube Data API v3. Deep insights into your channel performance.
              </p>
              
              {/* Channel Selection */}
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">SELECT CHANNEL</span>
                  <Youtube className="w-4 h-4 text-red-500" />
                </div>
                <div className="widget-content">
                  <div className="flex flex-wrap gap-2">
                    {channels.filter(ch => ch.platform === "youtube").map((ch) => (
                      <button
                        key={ch.id}
                        onClick={() => { setSelectedChannel(ch.id); fetchYoutubeAnalytics(ch.handle); }}
                        disabled={loading}
                        className={`px-4 py-2 border text-xs font-mono transition-colors ${selectedChannel === ch.id ? 'border-red-500 bg-red-500/10 text-red-500' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                      >
                        {loading && selectedChannel === ch.id ? (
                          <Loader2 className="w-3 h-3 animate-spin inline mr-2" />
                        ) : (
                          <Youtube className="w-3 h-3 inline mr-2" />
                        )}
                        @{ch.handle}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* YouTube OAuth Connection Widget */}
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">
                    <Lock className="w-4 h-4 inline mr-2" />
                    YOUTUBE ANALYTICS ACCESS
                  </span>
                  {youtubeOAuthStatus?.connected ? (
                    <span className="text-xs text-green-500 border border-green-500/30 px-2 py-0.5">CONNECTED</span>
                  ) : (
                    <span className="text-xs text-yellow-500 border border-yellow-500/30 px-2 py-0.5">NOT CONNECTED</span>
                  )}
                </div>
                <div className="widget-content">
                  {youtubeOAuthStatus?.connected ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30">
                        <div>
                          <p className="text-sm text-green-400 font-semibold">Connected to: {youtubeOAuthStatus.channel_title}</p>
                          <p className="text-xs text-zinc-500">Full analytics access: CTR, Watch Time, Retention, Traffic Sources</p>
                        </div>
                        <button
                          onClick={disconnectYoutubeAnalytics}
                          className="px-3 py-1 bg-red-500/20 border border-red-500/50 text-red-400 text-xs hover:bg-red-500/30"
                        >
                          DISCONNECT
                        </button>
                      </div>
                      
                      {selectedChannel && (
                        <button
                          onClick={() => fetchDetailedYoutubeAnalytics(selectedChannel)}
                          disabled={loading}
                          className="w-full py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-bold text-sm hover:from-purple-500 hover:to-cyan-500 transition-all flex items-center justify-center gap-2"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                          GET DETAILED ANALYTICS (CTR, RETENTION, TRAFFIC)
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-yellow-500/10 border border-yellow-500/30">
                        <p className="text-sm text-yellow-400 font-semibold mb-2">Unlock Full Analytics</p>
                        <p className="text-xs text-zinc-400 mb-3">
                          Connect your YouTube account to access the metrics the algorithm ACTUALLY uses:
                        </p>
                        <ul className="text-xs text-zinc-400 space-y-1 mb-4">
                          <li>• <span className="text-cyan-400">CTR (Click-Through Rate)</span> - How many people click your thumbnails</li>
                          <li>• <span className="text-cyan-400">Watch Time & Retention</span> - How long people actually watch</li>
                          <li>• <span className="text-cyan-400">Traffic Sources</span> - Where your views come from (Search, Suggested, Browse)</li>
                          <li>• <span className="text-cyan-400">Subscriber Behavior</span> - Who subscribes and who leaves</li>
                        </ul>
                        <button
                          onClick={connectYoutubeAnalytics}
                          className="w-full py-3 bg-gradient-to-r from-red-600 to-red-500 text-white font-bold text-sm hover:from-red-500 hover:to-red-400 transition-all flex items-center justify-center gap-2"
                        >
                          <Youtube className="w-4 h-4" />
                          CONNECT YOUTUBE ACCOUNT
                        </button>
                      </div>
                      {!youtubeOAuthStatus?.oauth_configured && (
                        <p className="text-xs text-red-400">
                          Note: YouTube OAuth credentials not configured. Contact the administrator.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Detailed Analytics Results */}
              {youtubeDetailedAnalytics && (
                <div className="space-y-6 mb-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-zinc-900 border border-cyan-500/30">
                      <p className="text-xs text-zinc-500 mb-1">CTR</p>
                      <p className="text-2xl font-bold text-cyan-400">{youtubeDetailedAnalytics.summary?.average_ctr_percent || 0}%</p>
                      <p className="text-xs text-zinc-600">{youtubeDetailedAnalytics.summary?.ctr_rating}</p>
                    </div>
                    <div className="p-4 bg-zinc-900 border border-green-500/30">
                      <p className="text-xs text-zinc-500 mb-1">WATCH TIME</p>
                      <p className="text-2xl font-bold text-green-400">{youtubeDetailedAnalytics.summary?.total_watch_time_hours || 0}h</p>
                      <p className="text-xs text-zinc-600">Last 30 days</p>
                    </div>
                    <div className="p-4 bg-zinc-900 border border-purple-500/30">
                      <p className="text-xs text-zinc-500 mb-1">AVG RETENTION</p>
                      <p className="text-2xl font-bold text-purple-400">{youtubeDetailedAnalytics.summary?.average_retention_percent || 0}%</p>
                      <p className="text-xs text-zinc-600">Avg view %</p>
                    </div>
                    <div className="p-4 bg-zinc-900 border border-yellow-500/30">
                      <p className="text-xs text-zinc-500 mb-1">NET SUBS</p>
                      <p className={`text-2xl font-bold ${(youtubeDetailedAnalytics.summary?.net_subscribers || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(youtubeDetailedAnalytics.summary?.net_subscribers || 0) >= 0 ? '+' : ''}{youtubeDetailedAnalytics.summary?.net_subscribers || 0}
                      </p>
                      <p className="text-xs text-zinc-600">Last 30 days</p>
                    </div>
                  </div>
                  
                  {/* Traffic Sources */}
                  <div className="widget">
                    <div className="widget-header">
                      <span className="widget-title">TRAFFIC SOURCES</span>
                      <span className="text-xs text-zinc-500">Where your views come from</span>
                    </div>
                    <div className="widget-content">
                      <div className="space-y-3">
                        {youtubeDetailedAnalytics.traffic_sources?.slice(0, 8).map((source, idx) => {
                          const totalViews = youtubeDetailedAnalytics.traffic_sources.reduce((sum, s) => sum + s.views, 0);
                          const percentage = totalViews > 0 ? (source.views / totalViews * 100).toFixed(1) : 0;
                          return (
                            <div key={idx} className="flex items-center gap-3">
                              <div className="flex-1">
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-zinc-300">{source.source}</span>
                                  <span className="text-zinc-500">{source.views.toLocaleString()} views ({percentage}%)</span>
                                </div>
                                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-cyan-500 to-purple-500" 
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Top Videos Performance */}
                  {youtubeDetailedAnalytics.top_videos?.length > 0 && (
                    <div className="widget">
                      <div className="widget-header">
                        <span className="widget-title">TOP VIDEOS PERFORMANCE</span>
                      </div>
                      <div className="widget-content">
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-zinc-800">
                                <th className="text-left p-2 text-zinc-500">VIDEO</th>
                                <th className="text-right p-2 text-zinc-500">VIEWS</th>
                                <th className="text-right p-2 text-zinc-500">CTR</th>
                                <th className="text-right p-2 text-zinc-500">AVG %</th>
                                <th className="text-right p-2 text-zinc-500">WATCH TIME</th>
                              </tr>
                            </thead>
                            <tbody>
                              {youtubeDetailedAnalytics.top_videos.map((video, idx) => (
                                <tr key={idx} className="border-b border-zinc-900">
                                  <td className="p-2 text-zinc-300 max-w-[200px] truncate">{video.video_id}</td>
                                  <td className="p-2 text-right text-cyan-400">{video.views?.toLocaleString()}</td>
                                  <td className="p-2 text-right text-green-400">{(video.ctr * 100).toFixed(1)}%</td>
                                  <td className="p-2 text-right text-purple-400">{video.avg_view_percentage?.toFixed(1)}%</td>
                                  <td className="p-2 text-right text-yellow-400">{Math.round(video.watch_time_minutes / 60)}h</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {loading && !youtubeAnalytics && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-red-500" />
                </div>
              )}
              
              {youtubeAnalytics && (
                <>
                  {/* Channel Overview */}
                  <div className="widget mb-6">
                    <div className="widget-header">
                      <span className="widget-title">CHANNEL OVERVIEW</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyToClipboard(formatReportForTTS(youtubeAnalytics, "youtube"))}
                          className="flex items-center gap-1 px-3 py-1 bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs hover:bg-zinc-700 hover:text-white transition-colors"
                          data-testid="copy-youtube-report-btn"
                        >
                          <Copy className="w-3 h-3" />
                          COPY REPORT
                        </button>
                        {youtubeAnalytics.search_visibility?.found_in_search ? (
                          <span className="text-green-500 text-xs flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> VISIBLE IN SEARCH
                          </span>
                        ) : (
                          <span className="text-red-500 text-xs flex items-center gap-1">
                            <XCircle className="w-3 h-3" /> NOT FOUND IN SEARCH
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="widget-content">
                      <div className="flex flex-col md:flex-row items-start gap-6 mb-6">
                        {youtubeAnalytics.channel?.thumbnail && (
                          <img 
                            src={youtubeAnalytics.channel.thumbnail} 
                            alt="Channel" 
                            className="w-20 h-20 rounded-full border-2 border-red-500"
                          />
                        )}
                        <div>
                          <h3 className="font-display font-bold text-2xl text-white">
                            {youtubeAnalytics.channel?.title}
                          </h3>
                          <p className="font-mono text-xs text-cyan-400">
                            {youtubeAnalytics.channel?.custom_url}
                          </p>
                          <p className="font-mono text-xs text-zinc-500 mt-1">
                            Channel ID: {youtubeAnalytics.channel_id}
                          </p>
                          <p className="font-mono text-[10px] text-zinc-600 mt-2 max-w-lg">
                            {youtubeAnalytics.channel?.description?.substring(0, 200)}...
                          </p>
                        </div>
                      </div>
                      
                      {/* Key Metrics */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-cyan-400" />
                            <span className="stat-label">SUBSCRIBERS</span>
                          </div>
                          <div className="stat-value text-2xl text-cyan-400">
                            {youtubeAnalytics.metrics?.subscribers?.toLocaleString()}
                          </div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="flex items-center gap-2 mb-2">
                            <Eye className="w-4 h-4 text-green-400" />
                            <span className="stat-label">TOTAL VIEWS</span>
                          </div>
                          <div className="stat-value text-2xl text-green-400">
                            {youtubeAnalytics.metrics?.total_views?.toLocaleString()}
                          </div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="flex items-center gap-2 mb-2">
                            <Video className="w-4 h-4 text-purple-400" />
                            <span className="stat-label">VIDEOS</span>
                          </div>
                          <div className="stat-value text-2xl text-purple-400">
                            {youtubeAnalytics.metrics?.video_count}
                          </div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <div className="flex items-center gap-2 mb-2">
                            <BarChart3 className="w-4 h-4 text-amber-400" />
                            <span className="stat-label">AVG VIEWS/VIDEO</span>
                          </div>
                          <div className="stat-value text-2xl text-amber-400">
                            {youtubeAnalytics.metrics?.avg_views_per_video?.toFixed(0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Search Visibility */}
                  <div className="widget mb-6">
                    <div className="widget-header">
                      <span className="widget-title">SEARCH VISIBILITY</span>
                    </div>
                    <div className="widget-content">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <span className="stat-label">FOUND IN SEARCH</span>
                          <div className={`stat-value text-xl mt-2 ${youtubeAnalytics.search_visibility?.found_in_search ? 'text-green-500' : 'text-red-500'}`}>
                            {youtubeAnalytics.search_visibility?.found_in_search ? 'YES' : 'NO'}
                          </div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <span className="stat-label">SEARCH POSITION</span>
                          <div className="stat-value text-xl mt-2 text-cyan-400">
                            {youtubeAnalytics.search_visibility?.search_position || 'N/A'}
                          </div>
                        </div>
                        <div className="bg-zinc-900 p-4 border border-zinc-800">
                          <span className="stat-label">TOTAL RESULTS</span>
                          <div className="stat-value text-xl mt-2 text-zinc-400">
                            {youtubeAnalytics.search_visibility?.total_search_results}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Algorithm Signals */}
                  <div className="widget mb-6">
                    <div className="widget-header">
                      <span className="widget-title">ALGORITHM SIGNALS</span>
                    </div>
                    <div className="widget-content">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className={`p-3 border ${youtubeAnalytics.algorithm_signals?.hidden_subscribers ? 'border-amber-500 bg-amber-500/10' : 'border-green-500 bg-green-500/10'}`}>
                          <span className="font-mono text-xs text-zinc-400">Hidden Subscribers</span>
                          <p className={`font-bold text-sm mt-1 ${youtubeAnalytics.algorithm_signals?.hidden_subscribers ? 'text-amber-500' : 'text-green-500'}`}>
                            {youtubeAnalytics.algorithm_signals?.hidden_subscribers ? 'YES' : 'NO'}
                          </p>
                        </div>
                        <div className={`p-3 border ${youtubeAnalytics.algorithm_signals?.made_for_kids ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-700'}`}>
                          <span className="font-mono text-xs text-zinc-400">Made for Kids</span>
                          <p className={`font-bold text-sm mt-1 ${youtubeAnalytics.algorithm_signals?.made_for_kids ? 'text-amber-500' : 'text-zinc-400'}`}>
                            {youtubeAnalytics.algorithm_signals?.made_for_kids ? 'YES' : 'NO'}
                          </p>
                        </div>
                        <div className={`p-3 border ${youtubeAnalytics.algorithm_signals?.long_uploads_enabled ? 'border-green-500 bg-green-500/10' : 'border-zinc-700'}`}>
                          <span className="font-mono text-xs text-zinc-400">Long Uploads</span>
                          <p className={`font-bold text-sm mt-1 ${youtubeAnalytics.algorithm_signals?.long_uploads_enabled ? 'text-green-500' : 'text-zinc-400'}`}>
                            {youtubeAnalytics.algorithm_signals?.long_uploads_enabled ? 'ENABLED' : 'NOT SET'}
                          </p>
                        </div>
                        <div className={`p-3 border ${youtubeAnalytics.algorithm_signals?.has_keywords ? 'border-green-500 bg-green-500/10' : 'border-amber-500 bg-amber-500/10'}`}>
                          <span className="font-mono text-xs text-zinc-400">Channel Keywords</span>
                          <p className={`font-bold text-sm mt-1 ${youtubeAnalytics.algorithm_signals?.has_keywords ? 'text-green-500' : 'text-amber-500'}`}>
                            {youtubeAnalytics.algorithm_signals?.has_keywords ? 'SET' : 'MISSING'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Recent Videos */}
                  <div className="widget">
                    <div className="widget-header">
                      <span className="widget-title">RECENT VIDEOS</span>
                      <span className="font-mono text-xs text-zinc-500">
                        Engagement Rate: {youtubeAnalytics.metrics?.avg_engagement_rate?.toFixed(2)}%
                      </span>
                    </div>
                    <ScrollArea className="h-[400px]">
                      <div className="widget-content space-y-4">
                        {youtubeAnalytics.recent_videos?.map((video, i) => (
                          <div key={video.video_id} className="flex gap-4 p-3 bg-zinc-900 border border-zinc-800">
                            {video.thumbnail && (
                              <img 
                                src={video.thumbnail} 
                                alt={video.title} 
                                className="w-32 h-20 object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-mono text-sm text-white truncate">{video.title}</h4>
                              <p className="font-mono text-[10px] text-zinc-500 mt-1">
                                {new Date(video.published_at).toLocaleDateString()}
                              </p>
                              <div className="flex items-center gap-4 mt-2">
                                <span className="flex items-center gap-1 text-xs text-cyan-400">
                                  <Eye className="w-3 h-3" />
                                  {video.statistics?.views?.toLocaleString()}
                                </span>
                                <span className="flex items-center gap-1 text-xs text-green-400">
                                  <ThumbsUp className="w-3 h-3" />
                                  {video.statistics?.likes?.toLocaleString()}
                                </span>
                                <span className="flex items-center gap-1 text-xs text-amber-400">
                                  <MessageCircle className="w-3 h-3" />
                                  {video.statistics?.comments?.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Instagram Analytics View */}
          {activeView === "instagram" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-display text-2xl text-white">INSTAGRAM ANALYTICS</h2>
                  <p className="text-sm text-zinc-500 mt-1">Real-time insights from Instagram Graph API. Deep insights into your Instagram performance.</p>
                </div>
                <Instagram className="w-8 h-8 text-pink-500" />
              </div>
              
              {/* Channel Selection */}
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">SELECT CHANNEL</span>
                  <Instagram className="w-4 h-4 text-pink-500" />
                </div>
                <div className="widget-content">
                  <div className="flex flex-wrap gap-2">
                    {channels.filter(ch => ch.platform === "instagram").map((ch) => (
                      <button
                        key={ch.id}
                        onClick={() => { setSelectedChannel(ch.id); }}
                        disabled={loading}
                        className={`px-4 py-2 border text-xs font-mono transition-colors ${selectedChannel === ch.id ? 'border-pink-500 bg-pink-500/10 text-pink-500' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                      >
                        {loading && selectedChannel === ch.id ? (
                          <Loader2 className="w-3 h-3 animate-spin inline mr-2" />
                        ) : (
                          <Instagram className="w-3 h-3 inline mr-2" />
                        )}
                        @{ch.handle}
                      </button>
                    ))}
                    {channels.filter(ch => ch.platform === "instagram").length === 0 && (
                      <p className="text-zinc-500 text-sm">No Instagram channels. Connect your account below to add one automatically.</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Instagram OAuth Connection Widget */}
              <div className="widget mb-6">
                <div className="widget-header">
                  <span className="widget-title">
                    <Lock className="w-4 h-4 inline mr-2" />
                    INSTAGRAM ANALYTICS ACCESS
                  </span>
                  {instagramOAuthStatus?.connected ? (
                    <span className="text-xs text-green-500 border border-green-500/30 px-2 py-0.5">CONNECTED</span>
                  ) : (
                    <span className="text-xs text-yellow-500 border border-yellow-500/30 px-2 py-0.5">NOT CONNECTED</span>
                  )}
                </div>
                <div className="widget-content">
                  {instagramOAuthStatus?.connected ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30">
                        <div>
                          <p className="text-sm text-green-400 font-semibold">Connected: @{instagramOAuthStatus.username}</p>
                          <p className="text-xs text-zinc-500">
                            {instagramOAuthStatus.followers_count?.toLocaleString()} followers • {instagramOAuthStatus.media_count} posts
                          </p>
                        </div>
                        <button
                          onClick={disconnectInstagram}
                          className="px-3 py-1 bg-red-500/20 border border-red-500/50 text-red-400 text-xs hover:bg-red-500/30"
                        >
                          DISCONNECT
                        </button>
                      </div>
                      
                      {selectedChannel && channels.find(c => c.id === selectedChannel)?.platform === "instagram" && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <button
                            onClick={() => fetchInstagramAnalytics(selectedChannel)}
                            disabled={loading}
                            className="py-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold text-sm hover:from-pink-500 hover:to-purple-500 transition-all flex items-center justify-center gap-2"
                          >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                            GET ANALYTICS
                          </button>
                          <button
                            onClick={() => fetchInstagramPerspective(selectedChannel)}
                            disabled={loading}
                            className="py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-bold text-sm hover:from-purple-500 hover:to-cyan-500 transition-all flex items-center justify-center gap-2"
                          >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                            ALGORITHM VIEW
                          </button>
                          <button
                            onClick={fetchInstagramNicheIntel}
                            disabled={loading}
                            className="py-3 bg-gradient-to-r from-cyan-600 to-green-600 text-white font-bold text-sm hover:from-cyan-500 hover:to-green-500 transition-all flex items-center justify-center gap-2"
                          >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
                            NICHE TRICKS
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30">
                        <p className="text-sm text-pink-400 font-semibold mb-2">Unlock Instagram Analytics</p>
                        <p className="text-xs text-zinc-400 mb-3">
                          Connect your Instagram Business/Creator account to access:
                        </p>
                        <ul className="text-xs text-zinc-400 space-y-1 mb-4">
                          <li>• <span className="text-pink-400">Reach & Impressions</span> - How many people see your content</li>
                          <li>• <span className="text-pink-400">Engagement Rate</span> - Likes, comments, saves, shares</li>
                          <li>• <span className="text-pink-400">Profile Views</span> - Who's checking you out</li>
                          <li>• <span className="text-pink-400">Top Posts Performance</span> - What's working</li>
                        </ul>
                        <button
                          onClick={connectInstagram}
                          className="w-full py-3 bg-gradient-to-r from-pink-600 via-purple-600 to-orange-500 text-white font-bold text-sm hover:from-pink-500 hover:via-purple-500 hover:to-orange-400 transition-all flex items-center justify-center gap-2"
                        >
                          <Instagram className="w-4 h-4" />
                          CONNECT INSTAGRAM BUSINESS
                        </button>
                        <p className="text-xs text-zinc-600 mt-2 text-center">
                          Requires Instagram Business or Creator account connected to a Facebook Page
                        </p>
                      </div>
                      {!instagramOAuthStatus?.oauth_configured && (
                        <p className="text-xs text-red-400">
                          Note: Instagram OAuth credentials not configured. Contact the administrator.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Instagram Analytics Results */}
              {instagramAnalytics && (
                <div className="space-y-6">
                  {/* Account Overview */}
                  <div className="widget">
                    <div className="widget-header">
                      <span className="widget-title">ACCOUNT OVERVIEW</span>
                      <span className="text-xs text-pink-400">{instagramAnalytics.data_source}</span>
                    </div>
                    <div className="widget-content">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-zinc-900 border border-pink-500/30">
                          <p className="text-xs text-zinc-500 mb-1">FOLLOWERS</p>
                          <p className="text-2xl font-bold text-pink-400">{instagramAnalytics.account?.followers?.toLocaleString() || 0}</p>
                        </div>
                        <div className="p-4 bg-zinc-900 border border-purple-500/30">
                          <p className="text-xs text-zinc-500 mb-1">FOLLOWING</p>
                          <p className="text-2xl font-bold text-purple-400">{instagramAnalytics.account?.following?.toLocaleString() || 0}</p>
                        </div>
                        <div className="p-4 bg-zinc-900 border border-cyan-500/30">
                          <p className="text-xs text-zinc-500 mb-1">TOTAL POSTS</p>
                          <p className="text-2xl font-bold text-cyan-400">{instagramAnalytics.account?.total_posts?.toLocaleString() || 0}</p>
                        </div>
                        <div className="p-4 bg-zinc-900 border border-green-500/30">
                          <p className="text-xs text-zinc-500 mb-1">ENGAGEMENT RATE</p>
                          <p className="text-2xl font-bold text-green-400">{instagramAnalytics.summary?.engagement_rate_percent || 0}%</p>
                          <p className="text-xs text-zinc-600">{instagramAnalytics.summary?.engagement_rating}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Key Metrics - Reach, Saves, Shares */}
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <div className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                      <p className="text-xs text-zinc-500 mb-1">REACH</p>
                      <p className="text-xl font-bold text-orange-400">{instagramAnalytics.summary?.total_reach?.toLocaleString() || 0}</p>
                    </div>
                    <div className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                      <p className="text-xs text-zinc-500 mb-1">LIKES</p>
                      <p className="text-xl font-bold text-pink-400">{instagramAnalytics.summary?.total_likes?.toLocaleString() || 0}</p>
                    </div>
                    <div className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                      <p className="text-xs text-zinc-500 mb-1">COMMENTS</p>
                      <p className="text-xl font-bold text-purple-400">{instagramAnalytics.summary?.total_comments?.toLocaleString() || 0}</p>
                    </div>
                    <div className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                      <p className="text-xs text-zinc-500 mb-1">SAVES</p>
                      <p className="text-xl font-bold text-yellow-400">{instagramAnalytics.summary?.total_saves?.toLocaleString() || 0}</p>
                    </div>
                    <div className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                      <p className="text-xs text-zinc-500 mb-1">SHARES</p>
                      <p className="text-xl font-bold text-cyan-400">{instagramAnalytics.summary?.total_shares?.toLocaleString() || 0}</p>
                    </div>
                    <div className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                      <p className="text-xs text-zinc-500 mb-1">POSTS</p>
                      <p className="text-xl font-bold text-zinc-300">{instagramAnalytics.summary?.posts_analyzed || 0}</p>
                    </div>
                  </div>
                  
                  {/* Demographics Section */}
                  {instagramAnalytics.demographics && Object.keys(instagramAnalytics.demographics).length > 0 && (
                    <div className="widget">
                      <div className="widget-header">
                        <span className="widget-title">AUDIENCE DEMOGRAPHICS</span>
                        <span className="text-xs text-zinc-500">From Instagram Graph API</span>
                      </div>
                      <div className="widget-content">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Age Distribution */}
                          {instagramAnalytics.demographics?.age?.length > 0 && (
                            <div>
                              <h4 className="text-xs text-pink-400 font-mono mb-3">AGE DISTRIBUTION</h4>
                              <div className="space-y-2">
                                {instagramAnalytics.demographics.age.map((item, idx) => {
                                  const total = instagramAnalytics.demographics.age.reduce((s, i) => s + i.value, 0);
                                  const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                                  return (
                                    <div key={idx} className="flex items-center gap-2">
                                      <span className="text-xs text-zinc-400 w-12">{item.label}</span>
                                      <div className="flex-1 h-4 bg-zinc-800 overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-pink-600 to-purple-600" style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className="text-xs text-zinc-400 w-16 text-right">{item.value} ({pct}%)</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Gender Distribution */}
                          {instagramAnalytics.demographics?.gender?.length > 0 && (
                            <div>
                              <h4 className="text-xs text-purple-400 font-mono mb-3">GENDER DISTRIBUTION</h4>
                              <div className="space-y-2">
                                {instagramAnalytics.demographics.gender.map((item, idx) => {
                                  const total = instagramAnalytics.demographics.gender.reduce((s, i) => s + i.value, 0);
                                  const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                                  const genderLabel = item.label === 'M' ? 'Male' : item.label === 'F' ? 'Female' : 'Unspecified';
                                  const genderColor = item.label === 'M' ? 'from-blue-600 to-cyan-600' : item.label === 'F' ? 'from-pink-600 to-rose-600' : 'from-zinc-600 to-zinc-500';
                                  return (
                                    <div key={idx} className="flex items-center gap-2">
                                      <span className="text-xs text-zinc-400 w-20">{genderLabel}</span>
                                      <div className="flex-1 h-4 bg-zinc-800 overflow-hidden">
                                        <div className={`h-full bg-gradient-to-r ${genderColor}`} style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className="text-xs text-zinc-400 w-16 text-right">{item.value} ({pct}%)</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Top Countries */}
                          {instagramAnalytics.demographics?.country?.length > 0 && (
                            <div>
                              <h4 className="text-xs text-cyan-400 font-mono mb-3">TOP COUNTRIES</h4>
                              <div className="space-y-2">
                                {instagramAnalytics.demographics.country.slice(0, 8).map((item, idx) => {
                                  const total = instagramAnalytics.demographics.country.reduce((s, i) => s + i.value, 0);
                                  const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                                  return (
                                    <div key={idx} className="flex items-center gap-2">
                                      <span className="text-xs text-zinc-400 w-8">{item.label}</span>
                                      <div className="flex-1 h-4 bg-zinc-800 overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-cyan-600 to-teal-600" style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className="text-xs text-zinc-400 w-16 text-right">{item.value} ({pct}%)</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Top Cities */}
                          {instagramAnalytics.demographics?.city?.length > 0 && (
                            <div>
                              <h4 className="text-xs text-green-400 font-mono mb-3">TOP CITIES</h4>
                              <div className="space-y-2">
                                {instagramAnalytics.demographics.city.slice(0, 8).map((item, idx) => {
                                  const total = instagramAnalytics.demographics.city.reduce((s, i) => s + i.value, 0);
                                  const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                                  return (
                                    <div key={idx} className="flex items-center gap-2">
                                      <span className="text-xs text-zinc-400 w-28 truncate" title={item.label}>{item.label}</span>
                                      <div className="flex-1 h-4 bg-zinc-800 overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-green-600 to-emerald-600" style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className="text-xs text-zinc-400 w-16 text-right">{item.value} ({pct}%)</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Top Posts with Rich Data */}
                  {instagramAnalytics.top_posts?.length > 0 && (
                    <div className="widget">
                      <div className="widget-header">
                        <span className="widget-title">TOP PERFORMING POSTS</span>
                        <span className="text-xs text-zinc-500">Sorted by total engagement</span>
                      </div>
                      <div className="widget-content">
                        <div className="space-y-3">
                          {instagramAnalytics.top_posts.map((post, idx) => (
                            <div key={idx} className="flex items-center gap-4 p-3 bg-zinc-900 border border-zinc-800">
                              <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-pink-500/20 text-pink-400 font-bold text-sm">
                                {idx + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-zinc-300 truncate">{post.caption || "No caption"}</p>
                                <p className="text-xs text-zinc-600">{post.media_type} {post.timestamp ? `- ${new Date(post.timestamp).toLocaleDateString()}` : ''}</p>
                              </div>
                              <div className="flex items-center gap-3 text-xs flex-shrink-0">
                                <span className="text-orange-400" title="Reach">{post.reach?.toLocaleString() || 0} reach</span>
                                <span className="text-pink-400" title="Likes">{post.likes?.toLocaleString() || 0} likes</span>
                                <span className="text-yellow-400" title="Saves">{post.saves?.toLocaleString() || 0} saves</span>
                                <span className="text-cyan-400" title="Shares">{post.shares?.toLocaleString() || 0} shares</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Instagram Algorithm Perspective */}
              {instagramPerspective && (
                <div className="widget">
                  <div className="widget-header">
                    <span className="widget-title">
                      <Eye className="w-4 h-4 inline mr-2 text-pink-500" />
                      INSTAGRAM ALGORITHM PERSPECTIVE
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-pink-400 border border-pink-500/30 px-2 py-0.5">
                        @{instagramPerspective.username}
                      </span>
                      <span className="text-xs text-cyan-500 border border-cyan-500/30 px-2 py-0.5">DUAL AI</span>
                    </div>
                  </div>
                  <div className="widget-content">
                    {instagramPerspective.gpt_perspective && instagramPerspective.claude_perspective ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="border border-green-500/20">
                          <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border-b border-green-500/20">
                            <div className="w-2 h-2 rounded-full bg-green-500" />
                            <span className="text-xs font-mono text-green-400">GPT-5.2 PERSPECTIVE</span>
                          </div>
                          <div className="p-3 ai-output text-sm whitespace-pre-wrap max-h-[500px] overflow-y-auto leading-relaxed">
                            {instagramPerspective.gpt_perspective}
                          </div>
                        </div>
                        <div className="border border-orange-500/20">
                          <div className="flex items-center gap-2 px-3 py-2 bg-orange-500/10 border-b border-orange-500/20">
                            <div className="w-2 h-2 rounded-full bg-orange-500" />
                            <span className="text-xs font-mono text-orange-400">CLAUDE PERSPECTIVE</span>
                          </div>
                          <div className="p-3 ai-output text-sm whitespace-pre-wrap max-h-[500px] overflow-y-auto leading-relaxed">
                            {instagramPerspective.claude_perspective}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="ai-output text-sm whitespace-pre-wrap max-h-[600px] overflow-y-auto leading-relaxed">
                        {instagramPerspective.algorithm_analysis}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Instagram Niche Intel */}
              {instagramNicheIntel && (
                <div className="space-y-6">
                  <div className="widget">
                    <div className="widget-header">
                      <span className="widget-title">
                        <Target className="w-4 h-4 inline mr-2 text-green-500" />
                        INSTAGRAM NICHE INTELLIGENCE - 2026 TACTICS
                      </span>
                    </div>
                    <div className="widget-content">
                      {/* Trending Tactics Grid */}
                      {instagramNicheIntel.trending_tactics && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          {/* Hot Formats */}
                          <div className="bg-zinc-900/50 p-4 border border-pink-500/30">
                            <h4 className="text-xs text-pink-400 font-mono mb-3">HOT FORMATS IN 2026</h4>
                            <ul className="space-y-2">
                              {instagramNicheIntel.trending_tactics.hot_formats?.map((format, idx) => (
                                <li key={idx} className="text-xs text-zinc-300 flex items-start gap-2">
                                  <span className="text-pink-500 mt-0.5">▸</span>
                                  {format}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          {/* Posting Strategy */}
                          <div className="bg-zinc-900/50 p-4 border border-purple-500/30">
                            <h4 className="text-xs text-purple-400 font-mono mb-3">OPTIMAL POSTING</h4>
                            <ul className="space-y-2">
                              {instagramNicheIntel.trending_tactics.optimal_posting?.map((tip, idx) => (
                                <li key={idx} className="text-xs text-zinc-300 flex items-start gap-2">
                                  <span className="text-purple-500 mt-0.5">◆</span>
                                  {tip}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          {/* Hashtag Strategy */}
                          <div className="bg-zinc-900/50 p-4 border border-cyan-500/30">
                            <h4 className="text-xs text-cyan-400 font-mono mb-3">HASHTAG STRATEGY</h4>
                            <ul className="space-y-2">
                              {instagramNicheIntel.trending_tactics.hashtag_strategy?.map((tip, idx) => (
                                <li key={idx} className="text-xs text-zinc-300 flex items-start gap-2">
                                  <span className="text-cyan-500 mt-0.5">#</span>
                                  {tip}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          {/* Growth Hacks */}
                          <div className="bg-zinc-900/50 p-4 border border-green-500/30">
                            <h4 className="text-xs text-green-400 font-mono mb-3">GROWTH HACKS</h4>
                            <ul className="space-y-2">
                              {instagramNicheIntel.trending_tactics.growth_hacks?.map((hack, idx) => (
                                <li key={idx} className="text-xs text-zinc-300 flex items-start gap-2">
                                  <span className="text-green-500 mt-0.5">⚡</span>
                                  {hack}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                      
                      {/* Full AI Analysis */}
                      <div className="widget">
                        <div className="widget-header">
                          <span className="widget-title">
                            <Brain className="w-4 h-4 inline mr-2 text-pink-500" />
                            FULL INTELLIGENCE REPORT
                          </span>
                        </div>
                        <div className="widget-content">
                          <div className="ai-output text-sm whitespace-pre-wrap max-h-[600px] overflow-y-auto leading-relaxed">
                            {instagramNicheIntel.tactics_discovered}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {loading && !instagramAnalytics && !instagramPerspective && !instagramNicheIntel && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      
      {/* Add Channel Dialog */}
      <Dialog open={showAddChannel} onOpenChange={setShowAddChannel}>
        <DialogContent className="bg-zinc-900 border-zinc-800" aria-describedby="add-channel-description">
          <DialogHeader>
            <DialogTitle className="font-display text-white">ADD NEW TARGET</DialogTitle>
            <p id="add-channel-description" className="sr-only">Add a new social media channel to track</p>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Quick-paste from any YouTube URL */}
            <div className="rounded border border-cyan-500/30 bg-cyan-500/5 p-3" data-testid="quick-paste-block">
              <label className="font-accent text-[10px] text-cyan-300 uppercase tracking-wider">Quick Paste (YouTube)</label>
              <p className="text-[10px] text-zinc-500 mt-1">Paste any YouTube link — channel page, video, /@handle, share URL. We&apos;ll auto-detect the channel.</p>
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  className="flex-1 bg-black border border-zinc-800 p-2 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-cyan-500 focus:outline-none"
                  placeholder="https://youtu.be/… or https://youtube.com/@handle"
                  value={quickUrl}
                  onChange={(e) => setQuickUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); resolveQuickUrl(); } }}
                  data-testid="quick-paste-input"
                  disabled={resolving}
                />
                <button
                  type="button"
                  onClick={resolveQuickUrl}
                  disabled={resolving || !quickUrl.trim()}
                  className="px-3 py-2 text-xs font-mono uppercase tracking-wider rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 hover:border-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  data-testid="quick-paste-detect-btn"
                >
                  {resolving ? "Detecting…" : "Detect"}
                </button>
              </div>
              {resolvedPreview && (
                <div className="mt-3 flex items-start gap-3 p-2 rounded bg-black/50 border border-cyan-500/20" data-testid="quick-paste-preview">
                  {resolvedPreview.thumbnail && (
                    <img src={resolvedPreview.thumbnail} alt="" className="w-12 h-12 rounded-full border border-cyan-500/30" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-white truncate">{resolvedPreview.title}</div>
                    <div className="text-[11px] text-zinc-400 truncate">@{resolvedPreview.handle} · {resolvedPreview.channel_id}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      {resolvedPreview.subscriber_count?.toLocaleString() || 0} subs · {resolvedPreview.video_count?.toLocaleString() || 0} videos{resolvedPreview.country ? ` · ${resolvedPreview.country}` : ""}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="font-accent text-[10px] text-zinc-600 uppercase tracking-wider">Platform</label>
              <select
                className="w-full mt-1 bg-black border border-zinc-800 p-2 font-mono text-sm text-white focus:border-red-500 focus:outline-none"
                value={newChannel.platform}
                onChange={(e) => setNewChannel({ ...newChannel, platform: e.target.value })}
              >
                <option value="youtube">YouTube</option>
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
              </select>
            </div>
            <div>
              <label className="font-accent text-[10px] text-zinc-600 uppercase tracking-wider">Handle</label>
              <input
                type="text"
                className="w-full mt-1 bg-black border border-zinc-800 p-2 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-red-500 focus:outline-none"
                placeholder="e.g., ruinsreplay or @ruinsreplay"
                value={newChannel.handle}
                onChange={(e) => setNewChannel({ ...newChannel, handle: e.target.value })}
                data-testid="add-channel-handle-input"
              />
              <p className="text-[10px] text-zinc-600 mt-1">You can paste with or without the @ — we clean it.</p>
            </div>
            <div>
              <label className="font-accent text-[10px] text-zinc-600 uppercase tracking-wider">URL (optional)</label>
              <input
                type="text"
                className="w-full mt-1 bg-black border border-zinc-800 p-2 font-mono text-sm text-white placeholder:text-zinc-600 focus:border-red-500 focus:outline-none"
                placeholder="Leave blank to auto-build from handle"
                value={newChannel.url}
                onChange={(e) => setNewChannel({ ...newChannel, url: e.target.value })}
                data-testid="add-channel-url-input"
              />
              <p className="text-[10px] text-zinc-600 mt-1">Tracking params like ?si=… are stripped automatically.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowAddChannel(false)} className="btn-secondary flex-1" data-testid="add-channel-cancel-btn">CANCEL</button>
              <button onClick={addChannel} className="btn-attack flex-1" data-testid="add-channel-submit-btn">ADD TARGET</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
